import { gsap } from 'gsap';
import remarkGfm from 'remark-gfm';
import EventBus from 'eventing-bus';
import { useChat } from '@ai-sdk/react';
import ReactMarkdown from 'react-markdown';
import { TextField, Switch } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';

import { SplitText } from 'gsap/SplitText';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
gsap.registerPlugin(SplitText, ScrollTrigger);

import './ThreadHistory.css';
import AudioInput from './AudioInput';
import MCPManager from './MCPManager';
import { renderChart } from './chart-tools';
import ThreadHistory from './ThreadHistory';
import { ApiUrl } from '../../store/config';
import { renderDocumentBlock } from './create-doc-tool';
import { setAssistant, setExpandChat, getAllMCPServers, connectMCPServer, setCurrentThread, disconnectMCPServer, getThreads, getThreadMessages } from '../../store/Assistant/index';
import { getIntegrationStatus, createIntegration, getIntegrations, getIntegrationTools } from '../../store/Integration';
// import { setAssistant, setExpandChat, getAllMCPServers, connectMCPServer, setCurrentThread, disconnectMCPServer, getThreadMessages, updateMessageFeedback} from '../../store/Assistant/index';

// Import static images
import webSearchIcon from '../../static/img/web-search.png';

const Assistant = () => {

  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const messagesEndRef = useRef(null);
  const headingRefs = useRef([]);
  const paragraphRefs = useRef([]);
  const splitParagraphs = useRef([]);
  const [copied, setCopied] = useState(false);
  const [mcpServer, setMcpServer] = useState({});
  const [toolFilter, setToolFilter] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [mcpManagerOpen, setMcpManagerOpen] = useState(false);
  const [dropdownOpenFiles, setDropdownOpenFiles] = useState(false);
  const [dropdownOpenTools, setDropdownOpenTools] = useState(false);
  const [collapsedTools, setCollapsedTools] = useState({});
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState('gpt-4-turbo-preview');

  // ================================== image dragand drop ========================
  // Add these to your existing state declarations
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [dragCounter, setDragCounter] = useState(0); // Add drag counter

  // ================================== Tool Mention System ========================
  const [showToolDropdown, setShowToolDropdown] = useState(false);
  const [mentionTrigger, setMentionTrigger] = useState(null); // '@' or '/'
  const [mentionFilter, setMentionFilter] = useState('');
  const [selectedToolIndex, setSelectedToolIndex] = useState(0);
  const [cursorPosition, setCursorPosition] = useState(0);
  const toolDropdownRef = useRef(null);

  // Available tools
  const availableTools = useMemo(() => [
    { id: 'bar-chart', name: 'Bar Chart', icon: '📊', keywords: ['bar', 'chart', 'graph', 'column'], toolName: 'createBarChart' },
    { id: 'line-chart', name: 'Line Chart', icon: '📈', keywords: ['line', 'chart', 'trend', 'time'], toolName: 'createLineChart' },
    { id: 'pie-chart', name: 'Pie Chart', icon: '🥧', keywords: ['pie', 'chart', 'donut', 'circle'], toolName: 'createPieChart' },
    { id: 'table', name: 'Table', icon: '📋', keywords: ['table', 'grid', 'data', 'rows'], toolName: 'createTable' },
    { id: 'document', name: 'Document', icon: '📄', keywords: ['document', 'doc', 'file', 'pdf'], toolName: 'createDocument' },
    { id: 'web-search', name: 'Web Search', icon: '🔍', keywords: ['search', 'web', 'google', 'find'], toolName: 'webSearch' }
  ], []);

  // Filter tools based on mention text
  const filteredTools = useMemo(() => {
    if (!mentionFilter) return availableTools;
    const search = mentionFilter.toLowerCase();
    return availableTools.filter(tool =>
      tool.name.toLowerCase().includes(search) ||
      tool.keywords.some(k => k.includes(search))
    );
  }, [mentionFilter, availableTools]);
  // ================================== Tool Mention System ========================

  const { auth } = useSelector(state => state['Auth']);
  const { userDetails, currentBranding } = useSelector(state => state['Settings']);
  const assistantModels = currentBranding?.assistantModels || [];
  const { isAssistant, isExpandChat, mcpServers, loading: mcpLoading, threadMessages, currentThreadId, messages: reduxMessages } = useSelector(state => state['Assistant']);

  // Use currentThreadId from Redux as the source of truth, fallback to a new thread ID
  // Also check localStorage for unsaved new chat threadId
  // Commented out: Original simple initialization
  // const [threadId, setThreadId] = useState(currentThreadId || `thread-${Date.now()}`);
  
  const getInitialThreadId = () => {
    if (currentThreadId) return currentThreadId;
    // Don't restore new chats from localStorage - they should be session-only
    // Only restore saved threads (not starting with 'thread-')
    const savedThreadId = localStorage.getItem('assistant_current_threadId');
    if (savedThreadId && !savedThreadId.startsWith('thread-')) {
      // This is a saved thread from backend, restore it
      return savedThreadId;
    }
    // Clear any new chat threadId from localStorage on mount
    if (savedThreadId && savedThreadId.startsWith('thread-')) {
      localStorage.removeItem('assistant_current_threadId');
    }
    // Always start with a fresh new chat threadId
    return `thread-${Date.now()}`;
  };
  
  const [threadId, setThreadId] = useState(getInitialThreadId());
  
  // Track previous threadId to detect actual thread changes (not just navigation)
  const previousThreadIdRef = useRef(currentThreadId || threadId);
  const shouldClearMessagesRef = useRef(false);

  // Persist input value across page navigations
  const STORAGE_KEY = 'milo_chat_input_draft';

  // Use the useChat hook for AI chat functionality
  const { messages: chatMessages, input, handleInputChange, handleSubmit, isLoading, error, reload, stop, setMessages } = useChat({
    id: threadId,
    api: `${ApiUrl}/automation/assistant/chat`,
    body: { threadId, model: selectedModel, toolChoice: 'auto' },
    headers: { 'Authorization': `bearer ${auth}`, 'Content-Type': 'application/json' },
    onError: (error) => {
      console.error('Chat error details:', error);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      console.error('Error cause:', error.cause);
      console.error('Full error object:', JSON.stringify(error, null, 2));

      // Show user-friendly error message
      const errorMessage = error?.message || 'An error occurred while processing your message';
      EventBus.publish('error', errorMessage);
    },
    onFinish: (message) => {
      // Clear the input draft when a message is successfully sent
      localStorage.removeItem(STORAGE_KEY);
      
      // When a message is finished, only save to Redux if it's a saved thread
      // New chats (starting with 'thread-') should NOT be saved to localStorage
      // They should only exist in the current session
      if (threadId && !currentThreadId) {
        // Only save to Redux if it's a saved thread (not a new chat)
        if (!threadId.startsWith('thread-')) {
          localStorage.setItem('assistant_current_threadId', threadId);
          dispatch(setCurrentThread(threadId));
          // Refresh threads list to include the new thread
          dispatch(getThreads());
        } else {
          // For new chats, just update Redux but don't save to localStorage
          // This allows the chat to work in the current session but won't persist
          dispatch(setCurrentThread(threadId));
        }
      }
    },
  });

  // Persist input value across page navigations - state management
  const previousInputRef = useRef('');
  const isRestoringRef = useRef(false);
  const hasMountedRef = useRef(false);

  // Restore input value from localStorage on mount
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      const savedInput = localStorage.getItem(STORAGE_KEY);
      if (savedInput && savedInput.trim()) {
        isRestoringRef.current = true;
        const event = { target: { value: savedInput } };
        handleInputChange(event);
        previousInputRef.current = savedInput;
        // Reset the flag after a short delay to allow the input to update
        setTimeout(() => {
          isRestoringRef.current = false;
        }, 100);
      }
    }
  }, [handleInputChange]); // Only run on mount, handleInputChange is stable from useChat

  // Save input value to localStorage whenever it changes (but not during restoration)
  useEffect(() => {
    if (!isRestoringRef.current && hasMountedRef.current) {
      if (input && input.trim()) {
        // Save non-empty input
        localStorage.setItem(STORAGE_KEY, input);
      } else if (!input && previousInputRef.current && previousInputRef.current.trim()) {
        // Input became empty after being non-empty - likely a message was sent
        // Clear localStorage only in this case
        localStorage.removeItem(STORAGE_KEY);
      }
      previousInputRef.current = input;
    }
  }, [input]);

  const handleCopy = () => {
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 3000); // 3 seconds
  };

  // Handle like/dislike feedback
  // const handleFeedback = useCallback((messageId, feedbackType) => {
  //   const message = displayMessages.find(msg => msg['_id'] || msg['id'] || msg['messageId'] === messageId);
  //   const currentFeedback = message?.feedback;
  //   const newFeedback = currentFeedback === feedbackType ? null : feedbackType;
  //   dispatch(updateMessageFeedback({ messageId, feedback: newFeedback }));
  // }, [dispatch]);

  // Handle speak 
  const handleSpeak = useCallback((content) => {
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(content);
    utterance.rate = 0.9;
    utterance.pitch = 1;
    utterance.volume = 1;

    window.speechSynthesis.speak(utterance);
  }, []);

  const toggleToolCollapse = useCallback((toolId) => {
    setCollapsedTools(prev => ({
      ...prev,
      [toolId]: !prev[toolId]
    }));
  }, []);
  const toggle = () => setDropdownOpen(prevState => !prevState);

  // Handle model selection
  const handleModelSelect = useCallback((model) => {
    setSelectedModel(model);
    setDropdownOpenFiles(false);
  }, []);
  // ================================== image dragand drop ========================
  // Add these handler functions
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === "dragenter") {
      setDragCounter(prev => prev + 1);
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragCounter(prev => {
        const newCount = prev - 1;
        if (newCount === 0) {
          setDragActive(false);
        }
        return newCount;
      });
    } else if (e.type === "dragover") {
      e.preventDefault();
      setDragActive(true);
    }
  };


  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    setDragCounter(0);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
      e.dataTransfer.clearData();
    }
  };
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    console.log("Files selected:", files);

    setSelectedFiles(files);

    // Create previews separately
    const previews = files.map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      url: URL.createObjectURL(file),
      type: file.type ? file.type.startsWith('image/') ? 'image' : 'file' : 'file',
      file: file,
    }));

    setFilePreviews(previews);
  };


  const handleFiles = (files) => {
    const fileArray = Array.from(files);

    const previews = fileArray.map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      url: URL.createObjectURL(file),
      type: file.type?.startsWith('image/') ? 'image' : 'file',
      file: file,
    }));

    setUploadedFiles(prev => [...prev, ...previews]);
    setFilePreviews(prev => [...prev, ...previews]);
  };

  const removeFile = (id) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== id));
    setFilePreviews(prev => prev.filter(file => file.id !== id));
  };

  // Add this useEffect to clean up object URLs
  useEffect(() => {
    return () => {
      uploadedFiles.forEach(file => URL.revokeObjectURL(file.preview));
    };
  }, [uploadedFiles]);
  // ================================== image dragand drop ========================

  // Combine historical messages from Redux with new chat messages
  const displayMessages = useMemo(() => {
    console.log('DisplayMessages Debug:', {
      threadId,
      currentThreadId,
      chatMessagesCount: chatMessages.length,
      threadMessagesKeys: Object.keys(threadMessages),
      hasMessagesForThreadId: !!threadMessages[threadId],
      hasMessagesForCurrentThread: !!threadMessages[currentThreadId],
      reduxMessagesCount: reduxMessages?.length || 0
    });

    // Try multiple ways to get historical messages
    let historicalMessages = [];

    // First try currentThreadId
    if (currentThreadId && threadMessages[currentThreadId]) {
      historicalMessages = threadMessages[currentThreadId];
      console.log('Found messages using curr  entThreadId:', historicalMessages.length);
    }
    // Then try threadId
    else if (threadId && threadMessages[threadId]) {
      historicalMessages = threadMessages[threadId];
      console.log('Found messages using threadId:', historicalMessages.length);
    }
    // Finally try reduxMessages as fallback
    else if (reduxMessages && reduxMessages.length > 0) {
      historicalMessages = reduxMessages;
      console.log('Using reduxMessages as fallback:', historicalMessages.length);
    }

    if (historicalMessages.length > 0) {
      const formattedHistoricalMessages = historicalMessages.map(msg => ({
        id: msg['messageId'] || msg['_id'] || msg['id'],
        messageId: msg['messageId'],
        role: msg['role'],
        content: msg['content'],
        createdAt: msg['createdAt'],
        ...msg
      }));

      // Filter out any chat messages that might be duplicates based on content
      const newMessages = chatMessages.filter(chatMsg =>
        !formattedHistoricalMessages.some(histMsg =>
          histMsg['content'] === chatMsg['content'] && histMsg['role'] === chatMsg['role']
        )
      );

      console.log('Returning combined messages:', formattedHistoricalMessages.length + newMessages.length);
      return [...formattedHistoricalMessages, ...newMessages];
    }

    // If no historical messages, just use chat messages
    console.log('No historical messages found, using chat messages only');
    return chatMessages;
  }, [chatMessages, threadMessages, currentThreadId, threadId, reduxMessages]);

  useEffect(() => {
    if (isAssistant) {
      // Load MCP servers and tools when assistant opens
      dispatch(getAllMCPServers());
    }
    // Focus input if assistant is active and history is closed
    if (isAssistant && !showHistory && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isAssistant, showHistory]);

  // Handle click outside for tool dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (toolDropdownRef.current && !toolDropdownRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setShowToolDropdown(false);
      }
    };

    if (showToolDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showToolDropdown]);

  // Clear new chats when assistant is closed (navigating away)
  // This ensures new chats don't persist across navigation
  useEffect(() => {
    if (!isAssistant) {
      // If assistant is closed and we have a new chat (not a saved thread), clear it
      if (threadId && threadId.startsWith('thread-') && !currentThreadId) {
        // Only clear if we haven't already processed this cleanup
        // Check if this threadId is different from what we last cleaned up
        if (previousThreadIdRef.current !== threadId) {
          // Clear the new chat threadId from localStorage if it exists
          const savedThreadId = localStorage.getItem('assistant_current_threadId');
          if (savedThreadId && savedThreadId.startsWith('thread-')) {
            localStorage.removeItem('assistant_current_threadId');
          }
          // Clear Redux state for new chat
          dispatch(setCurrentThread(null));
          // Clear messages
          if (setMessages) {
            setMessages([]);
          }
          // Reset to a new threadId for next time
          const newThreadId = `thread-${Date.now()}`;
          setThreadId(newThreadId);
          previousThreadIdRef.current = newThreadId;
        }
      }
    }
  }, [isAssistant, currentThreadId]);

  // Watch for sidebar state changes and update body class
  useEffect(() => {
    const checkSidebarState = () => {
      const sidebar = document.querySelector('.ps-sidebar-root');
      if (sidebar) {
        if (sidebar.classList.contains('ps-collapsed')) {
          document.body.classList.add('sidebar-collapsed');
          document.body.classList.remove('sidebar-expanded');
        } else {
          document.body.classList.add('sidebar-expanded');
          document.body.classList.remove('sidebar-collapsed');
        }
      }
    };

    // Check initial state
    checkSidebarState();

    // Watch for changes using MutationObserver
    const sidebar = document.querySelector('.ps-sidebar-root');
    if (sidebar) {
      const observer = new MutationObserver(checkSidebarState);
      observer.observe(sidebar, {
        attributes: true,
        attributeFilter: ['class']
      });

      return () => observer.disconnect();
    }
  }, []);

  useEffect(() => {
    // Auto-scroll to bottom when new messages arrive
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [displayMessages]);

  // Refresh messages from backend when chat completes
  // useEffect(() => {
  //   if (!isLoading && threadId && chatMessages.length > 0) {
  //     const timer = setTimeout(() => dispatch(getThreadMessages(threadId)), 100);
  //     return () => clearTimeout(timer);
  //   }
  // }, [isLoading, threadId, dispatch]);

  // Handle thread switching
  // COMMENTED OUT: This was clearing messages during navigation between screens
  // Now messages will only be cleared when:
  // 1. User clicks "Clear chat" button (clearChat function)
  // 2. User refreshes the page (natural state reset)
  // 3. User explicitly selects a different thread from history
  // useEffect(() => {
  //   if (currentThreadId) {
  //     setThreadId(currentThreadId);
  //     if (setMessages) {
  //       setMessages([]);
  //     }
  //   }
  // }, [currentThreadId, setMessages]);

  // Updated: Only update threadId when currentThreadId changes, but don't clear messages
  // Messages will persist during navigation and only clear when explicitly requested
  useEffect(() => {
    if (currentThreadId && currentThreadId !== previousThreadIdRef.current) {
      // Only clear messages if this is an intentional thread change (not just navigation)
      if (shouldClearMessagesRef.current) {
        setThreadId(currentThreadId);
        if (setMessages) {
          setMessages([]);
        }
        shouldClearMessagesRef.current = false; // Reset the flag
        // Clear localStorage when switching to a saved thread
        localStorage.removeItem('assistant_current_threadId');
      } else {
        // Just update the threadId without clearing messages (preserve chat during navigation)
        setThreadId(currentThreadId);
      }
      previousThreadIdRef.current = currentThreadId;
    } else if (!currentThreadId && previousThreadIdRef.current) {
      // If currentThreadId is cleared, update the ref but don't clear messages
      previousThreadIdRef.current = null;
    }
  }, [currentThreadId, setMessages]);

  // Don't save new chats to localStorage - they should be session-only
  // Only saved threads (from history) should persist
  // COMMENTED OUT: This effect was removed to prevent new chats from persisting
  // useEffect(() => {
  //   if (threadId && chatMessages.length > 0 && !currentThreadId) {
  //     // Only save if this is a new chat (not a saved thread from history)
  //     // Check if threadId starts with 'thread-' (temporary ID pattern)
  //     if (threadId.startsWith('thread-')) {
  //       localStorage.setItem('assistant_current_threadId', threadId);
  //       // Also update Redux to persist the threadId
  //       dispatch(setCurrentThread(threadId));
  //     }
  //   }
  // }, [threadId, chatMessages.length, currentThreadId, dispatch]);

  // On mount, only restore saved threads (not new chats)
  useEffect(() => {
    const savedThreadId = localStorage.getItem('assistant_current_threadId');
    // Only restore if it's a saved thread (not a new chat starting with 'thread-')
    if (savedThreadId && savedThreadId === threadId && !currentThreadId && !savedThreadId.startsWith('thread-')) {
      // If we restored a saved threadId, set it in Redux and try to load messages from backend
      dispatch(setCurrentThread(savedThreadId));
      if (chatMessages.length === 0) {
        dispatch(getThreadMessages(savedThreadId));
      }
    }
  }, []); // Only run on mount

  // const handleFormSubmit = (e) => {
  //   e.preventDefault();
  //   if (!input.trim() || isLoading) return;
  //   handleSubmit(e);
  //   // Delay focus till after re-render
  //   setTimeout(() => {
  //     if (inputRef.current) {
  //       inputRef.current.focus();
  //     }
  //   }, 0);
  // };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (filePreviews.length > 0) {
      try {
        // Convert files to base64
        const attachments = await Promise.all(
          filePreviews.map(async (filePreview) => {
            console.log(filePreviews, "hhhhhh")
            const base64Data = await convertFileToBase64(filePreview.file);

            return {
              name: filePreview.name,
              contentType: filePreview.file.type,
              size: filePreview.file.size,
              data: base64Data, // This will be: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAA..."
            };
          })
        );

        console.log("Base64 attachments:", attachments);

        handleSubmit(e, {
          experimental_attachments: attachments,
        });
      } catch (error) {
        console.error("Error converting files to base64:", error);
      }
    } else {
      handleSubmit(e);
    }

    // Clean up
    setFilePreviews([]);

    filePreviews.forEach(preview => {
      if (preview.url.startsWith('blob:')) {
        URL.revokeObjectURL(preview.url);
      }
    });
  };


  //   const handleFormSubmit = (e) => {
  //   e.preventDefault();

  //   // Prepare attachments from your file previews
  //   const attachments = filePreviews.map(filePreview => {
  //     // You can send either the File object directly or create attachment metadata
  //     console.log(filePreviews,"file preview")
  //     return {
  //       name: filePreview.name,
  //       contentType: filePreview.type,
  //       url: filePreview.url,
  //       file: filePreview.file
  //     };
  //   });

  //   // Submit with attachments
  //   handleSubmit(e, {
  //     experimental_attachments: attachments.length > 0 ? attachments : undefined,
  //   });
  //     console.log(attachments,"attach")

  //   // Clean up
  //   setFilePreviews([]);

  //   // Clean up object URLs to prevent memory leaks
  //   filePreviews.forEach(preview => {
  //     if (preview.url.startsWith('blob:')) {
  //       URL.revokeObjectURL(preview.url);
  //     }
  //   });
  // };



  const handleTranscriptionComplete = useCallback((transcribedText) => {
    if (transcribedText) {
      // Set the transcribed text as input
      const event = { target: { value: transcribedText } };
      handleInputChange(event);
      // Focus the input field
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [handleInputChange]);

  const handleQuickAction = useCallback((action) => {
    const event = { target: { value: action }, preventDefault: () => { } };
    handleInputChange(event);
    setTimeout(() => {
      const form = document.querySelector('.massage-box');
      if (form) form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }, 100);
  }, [handleInputChange]);

  const handlePromptClick = useCallback((promptText) => {
    // Fill the input with the prompt text
    const event = { target: { value: promptText } };
    handleInputChange(event);

    // Focus the input field so user can edit or send
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
  }, [handleInputChange]);

  // ======================== Tool Mention Handlers ========================
  const handleCustomInputChange = useCallback((e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;

    // Call original handler
    handleInputChange(e);

    // Check for @ or / trigger
    const textBeforeCursor = value.substring(0, cursorPos);
    const words = textBeforeCursor.split(/\s/);
    const lastWord = words[words.length - 1];

    if (lastWord.startsWith('@') || lastWord.startsWith('/')) {
      const trigger = lastWord[0];
      const filterText = lastWord.substring(1);
      setMentionTrigger(trigger);
      setMentionFilter(filterText);
      setShowToolDropdown(true);
      setSelectedToolIndex(0);
      setCursorPosition(cursorPos);
    } else {
      setShowToolDropdown(false);
    }
  }, [handleInputChange]);

  const insertToolMention = useCallback((tool) => {
    const inputElement = inputRef.current?.querySelector('input') || inputRef.current;
    if (!inputElement) return;

    const value = input;
    const cursorPos = cursorPosition;

    // Find the trigger position
    const textBeforeCursor = value.substring(0, cursorPos);
    const triggerIndex = textBeforeCursor.lastIndexOf(mentionTrigger);

    if (triggerIndex !== -1) {
      const before = value.substring(0, triggerIndex);
      const after = value.substring(cursorPos);
      const toolMention = `${mentionTrigger}${tool.name}`;
      const newValue = before + toolMention + ' ' + after;

      // Update the input
      const event = { target: { value: newValue } };
      handleInputChange(event);

      // Set cursor position after mention
      setTimeout(() => {
        const newCursorPos = triggerIndex + toolMention.length + 1;
        if (inputElement.setSelectionRange) {
          inputElement.setSelectionRange(newCursorPos, newCursorPos);
        }
        inputElement.focus();
      }, 0);
    }

    setShowToolDropdown(false);
    setMentionTrigger(null);
    setMentionFilter('');
  }, [input, cursorPosition, mentionTrigger, handleInputChange]);

  const handleKeyDown = useCallback((e) => {
    // Handle tool dropdown navigation
    if (showToolDropdown) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedToolIndex(prev => (prev + 1) % filteredTools.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedToolIndex(prev => (prev - 1 + filteredTools.length) % filteredTools.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (filteredTools[selectedToolIndex]) {
          insertToolMention(filteredTools[selectedToolIndex]);
        }
        return;
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setShowToolDropdown(false);
        return;
      }
    }

    // Handle Enter key behavior for message submission
    if (e.key === 'Enter' && !showToolDropdown) {
      if (e.shiftKey) {
        // Shift+Enter: Add a new line (allow default behavior)
        // We don't prevent default here to allow the line break
      } else {
        // Enter (without Shift): Submit the form
        e.preventDefault();
        if (input.trim()) {
          handleFormSubmit(e);
        }
      }
    }
  }, [input, handleFormSubmit, showToolDropdown, filteredTools, selectedToolIndex, insertToolMention]);
  // ======================== Tool Mention Handlers ========================

  const clearChat = useCallback(() => {
    // For new chats, we'll use a temporary ID that the backend will handle
    const newThreadId = `thread-${Date.now()}`;
    setThreadId(newThreadId);
    previousThreadIdRef.current = newThreadId; // Update the ref
    dispatch(setCurrentThread(null)); // Clear current thread in Redux
    // Clear localStorage - but only if it's a new chat
    // Don't clear saved threads from history
    const savedThreadId = localStorage.getItem('assistant_current_threadId');
    if (savedThreadId && savedThreadId.startsWith('thread-')) {
      localStorage.removeItem('assistant_current_threadId');
    }
    // Clear messages in the chat UI
    if (setMessages) {
      setMessages([]);
    }
  }, [dispatch, setMessages]);

  const connectServer = useCallback((serverName) => dispatch(connectMCPServer(serverName)), [dispatch]);
  const formatMessage = useCallback((message) => {
    if (message['attachments'] && message['attachments'].length > 0) {
      return (
        <div>
          {message['attachments'].map((attachment, idx) => {
            if (attachment?.contentType?.startsWith?.('image/')) {
              return (
                <div key={idx} className="attachment-container">
                  <img
                    src={attachment.data}
                    alt={attachment.name}
                    className="message-image"
                    style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '8px' }}
                  />
                </div>
              );
            }
            return null;
          })}
          {/* Rest of your message formatting */}
          {message['content'] && (
            <article className="markdown-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {message['content']}
              </ReactMarkdown>
            </article>
          )}
        </div>
      );
    }
    if (message['toolInvocations'] && message['toolInvocations'].length > 0) {
      return (
        <div>
          {/* Tool invocations first */}
          {message['toolInvocations'].map((tool, idx) => {
            if (tool['toolName'] === 'createDocument' && tool['result']?.success) {
              return renderDocumentBlock(tool, idx);
            }

            if (tool['result'] && (tool['toolName'].includes('Chart') || tool['result'].chartType)) {
              return renderChart(tool['result']);
            }

            const toolId = `tool-${idx}-${tool['toolName']}`;
            const isCollapsed = collapsedTools[toolId];
            const resultText = typeof tool['result'] === 'string' ? tool['result'] : JSON.stringify(tool['result'], null, 2);
            const isLongResult = resultText && resultText.length > 300;

            return (
              <div className='expande-btn' key={idx} style={{
                marginTop: idx === 0 ? '0' : '12px',
                marginBottom: '12px',
                padding: '12px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #CAD6E4',
              }}>
                <div className='expande-btn-box' style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: tool['result'] ? '8px' : '0'
                }}>
                  <span className='icon' style={{
                    fontSize: '16px',
                    color: '#3b82f6'
                  }}>🔧</span>
                  <strong style={{
                    color: '#1e40af',
                    fontSize: '14px',
                    flex: 1
                  }}>
                    {tool['toolName']}
                  </strong>
                  <span className='result-btn' style={{
                    fontSize: '12px',
                    color: 'var(--primary-color)',
                    backgroundColor: tool['state'] === 'completed' ? '#d1fae5' : '#e5e7eb',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    border: tool['state'] === 'completed' ? '1px solid #a7f3d0' : '1px solid #d1d5db'
                  }}>
                    {tool['state'] || 'completed'}
                  </span>
                  {tool['result'] && isLongResult && (
                    <button
                      onClick={() => toggleToolCollapse(toolId)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#6b7280',
                        cursor: 'pointer',
                        fontSize: '12px',
                        padding: '2px 4px',
                        borderRadius: '3px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2px'
                      }}
                    >
                      {isCollapsed ? '▶' : '▼'}
                      {isCollapsed ? 'Show' : 'Hide'}
                    </button>
                  )}
                </div>
                {tool['result'] && (!isLongResult || !isCollapsed) && (
                  <div style={{
                    marginTop: '8px',
                    padding: '8px',
                    backgroundColor: '#ffffff',
                    borderRadius: '4px',
                    border: '1px solid #e5e7eb',
                    maxHeight: isLongResult && !isCollapsed ? '200px' : 'none',
                    overflow: 'auto'
                  }}>
                    <pre style={{
                      fontSize: '12px',
                      margin: '0',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      color: '#374151',
                      fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                      lineHeight: '1.4'
                    }}>
                      {resultText}
                    </pre>
                  </div>
                )}
                {tool['result'] && isLongResult && isCollapsed && (
                  <div className='result-text' style={{
                    marginTop: '8px',
                    padding: '8px',
                    backgroundColor: '#f9fafb',
                    borderRadius: '4px',
                    border: '1px solid #e5e7eb',
                    fontSize: '12px',
                    color: '#6b7280',
                    fontStyle: 'italic'
                  }}>
                    Tool result hidden (click Show to expand)
                  </div>
                )}
              </div>
            );
          })}

          {/* AI explanation text after tool results */}
          <div>
            {/* Use backend-processed content if available, otherwise process with ReactMarkdown */}
            {message['formattedContent'] && message['hasMarkdownFormatting'] ? (
              <div
                className="formatted-content"
                dangerouslySetInnerHTML={{ __html: message['formattedContent'] }}
              />
            ) : (
              <article className="markdown-content">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    strong: ({ children }) => (
                      <span style={{ fontWeight: '600', color: 'inherit' }}>{children}</span>
                    ),
                    em: ({ children }) => (
                      <span style={{ fontStyle: 'italic', color: 'inherit' }}>{children}</span>
                    ),
                    p: ({ children }) => (
                      <p style={{
                        margin: '0.5rem 0',
                        lineHeight: '1.6',
                        color: 'inherit',
                        wordBreak: 'break-word',
                        whiteSpace: 'normal',
                        overflowWrap: 'break-word',
                        textAlign: 'left'
                      }}>{children}</p>
                    ),
                    ul: ({ children }) => (
                      <ul style={{
                        margin: '0.5rem 0',
                        paddingLeft: '1.5rem',
                        listStyleType: 'disc',
                        listStylePosition: 'outside',
                        color: 'inherit'
                      }}>{children}</ul>
                    ),
                    ol: ({ children }) => (
                      <ol style={{
                        margin: '0.5rem 0',
                        paddingLeft: '1.5rem',
                        listStyleType: 'decimal',
                        listStylePosition: 'outside',
                        color: 'inherit'
                      }}>{children}</ol>
                    ),
                    li: ({ children }) => (
                      <li style={{
                        margin: '0.3rem 0',
                        lineHeight: '1.6',
                        color: 'inherit',
                        wordBreak: 'break-word',
                        whiteSpace: 'normal',
                        overflowWrap: 'break-word'
                      }}>{children}</li>
                    ),
                    h1: ({ children }) => (
                      <h1 style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        margin: '0.6rem 0 0.3rem',
                        color: 'var(--primary-color)',
                        lineHeight: '1.3'
                      }}>{children}</h1>
                    ),
                    h2: ({ children }) => (
                      <h2 style={{
                        fontSize: '15px',
                        fontWeight: '600',
                        margin: '0.5rem 0 0.25rem',
                        color: 'var(--primary-color)',
                        lineHeight: '1.3'
                      }}>{children}</h2>
                    ),
                    h3: ({ children }) => (
                      <h3 style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        margin: '0.4rem 0 0.2rem',
                        color: 'var(--primary-color)',
                        lineHeight: '1.3'
                      }}>{children}</h3>
                    ),
                    blockquote: ({ children }) => (
                      <blockquote style={{
                        borderLeft: '4px solid #CAD6E4',
                        paddingLeft: '1rem',
                        margin: '1.5rem 0',
                        color: '#64748b',
                        fontStyle: 'italic',
                        backgroundColor: 'rgba(0,0,0,0.02)',
                        borderRadius: '0.5rem',
                        padding: '1rem 1rem 1rem 1.5rem'
                      }}>{children}</blockquote>
                    ),
                    code: ({ children }) => (
                      <code style={{
                        backgroundColor: '#f1f5f9',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '0.25rem',
                        fontFamily: 'ui-monospace, SFMono-Regular, Monaco, Consolas, "Courier New", monospace',
                        fontSize: '0.875rem',
                        border: '1px solid #CAD6E4'
                      }}>{children}</code>
                    ),
                    pre: ({ children }) => (
                      <pre style={{
                        backgroundColor: '#f8fafc',
                        padding: '1rem',
                        borderRadius: '0.5rem',
                        overflow: 'auto',
                        fontFamily: 'ui-monospace, SFMono-Regular, Monaco, Consolas, "Courier New", monospace',
                        margin: '1.5rem 0',
                        border: '1px solid #CAD6E4'
                      }}>{children}</pre>
                    ),
                  }}
                >
                  {message['content']}
                </ReactMarkdown>
              </article>

            )}
          </div>
        </div>
      );
    }

    // For regular messages, use backend-processed content if available
    if (message['formattedContent'] && message['hasMarkdownFormatting']) {
      return (
        <div
          className="formatted-content"
          dangerouslySetInnerHTML={{ __html: message['formattedContent'] }}
        />
      );
    }

    // Fallback to ReactMarkdown for client-side processing
    return (
      <article className="markdown-content">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            // Enhanced styling based on better-chatbot patterns
            strong: ({ children }) => (
              <span style={{ fontWeight: '600', color: 'inherit' }}>{children}</span>
            ),
            em: ({ children }) => (
              <span style={{ fontStyle: 'italic', color: 'inherit' }}>{children}</span>
            ),
            p: ({ children }) => (
              <p style={{
                /* margin: '0.3rem 0', */
                margin: '0.5rem 0',
                /* lineHeight: '1.4', */
                lineHeight: '1.6',
                color: 'inherit',
                /* wordBreak: 'break-words' */
                wordBreak: 'break-word',
                whiteSpace: 'normal',
                overflowWrap: 'break-word',
                textAlign: 'left'
              }}>{children}</p>
            ),
            ul: ({ children }) => (
              <ul style={{
                margin: '0.2rem 0',
                paddingLeft: '1.2rem',
                listStyleType: 'disc',
                listStylePosition: 'outside',
                color: 'inherit'
              }}>{children}</ul>
            ),
            ol: ({ children }) => (
              <ol style={{
                margin: '0.2rem 0',
                paddingLeft: '1.5rem',
                listStyleType: 'decimal',
                listStylePosition: 'outside',
                color: 'inherit'
              }}>{children}</ol>
            ),
            li: ({ children }) => (
              <li style={{
                /* margin: '0.2rem 0', */
                margin: '0.3rem 0',
                /* lineHeight: '1.4', */
                lineHeight: '1.6',
                display: 'list-item',
                color: 'inherit',
                /* wordBreak: 'break-words', */
                wordBreak: 'break-word',
                whiteSpace: 'normal',
                overflowWrap: 'break-word',
                paddingLeft: '0.2rem'
              }}>{children}</li>
            ),
            h1: ({ children }) => (
              <h1 style={{
                fontSize: '1.3rem',
                fontWeight: '600',
                margin: '0.4rem 0 0.2rem',
                color: 'inherit',
                lineHeight: '1.2'
              }}>{children}</h1>
            ),
            h2: ({ children }) => (
              <h2 style={{
                fontSize: '1.2rem',
                fontWeight: '600',
                margin: '0.3rem 0 0.15rem',
                color: 'inherit',
                lineHeight: '1.2'
              }}>{children}</h2>
            ),
            h3: ({ children }) => (
              <h3 style={{
                fontSize: '1.1rem',
                fontWeight: '600',
                margin: '0.25rem 0 0.1rem',
                color: 'inherit',
                lineHeight: '1.2'
              }}>{children}</h3>
            ),
            blockquote: ({ children }) => (
              <blockquote style={{
                borderLeft: '4px solid #CAD6E4',
                paddingLeft: '1rem',
                margin: '1.5rem 0',
                color: '#64748b',
                fontStyle: 'italic',
                backgroundColor: 'rgba(0,0,0,0.02)',
                borderRadius: '0.5rem',
                padding: '1rem 1rem 1rem 1.5rem'
              }}>{children}</blockquote>
            ),
            code: ({ children }) => (
              <code style={{
                backgroundColor: '#f1f5f9',
                padding: '0.25rem 0.5rem',
                borderRadius: '0.25rem',
                fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                fontSize: '0.875rem',
                border: '1px solid #CAD6E4'
              }}>{children}</code>
            ),
            pre: ({ children }) => (
              <pre style={{
                backgroundColor: '#f8fafc',
                padding: '1rem',
                borderRadius: '0.5rem',
                overflow: 'auto',
                fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                margin: '1.5rem 0',
                border: '1px solid #CAD6E4'
              }}>{children}</pre>
            ),
          }}
        >
          {message['content']}
        </ReactMarkdown>
      </article>
    );
  }, [collapsedTools, toggleToolCollapse]);



  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // useEffect(() => {
  //   document.title = showHistory ? 'Conversations' : 'New Chat';
  // }, [showHistory]);

  useEffect(() => {
    if (!isLoading && !mcpLoading && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLoading, mcpLoading]);

  const setHeadingRef = (el, index) => {
    if (el) headingRefs.current[index] = el;
  };

  // Utility to collect multiple paragraph refs
  const setParagraphRef = (el, index) => {
    if (el) paragraphRefs.current[index] = el;
  };

  useEffect(() => {
    if (isAssistant) {
      // Animate Headings with incremental delay
      headingRefs.current.forEach((el, i) => {
        gsap.fromTo(
          el,
          { opacity: 0, filter: 'blur(10px)', y: -20 },
          {
            opacity: 1,
            filter: 'blur(0px)',
            y: 0,
            duration: 0.8,
            ease: 'power3.out',
            delay: i * 0.3, // delay per element
          }
        );
      });

      // Clear previous SplitText instances
      splitParagraphs.current.forEach((split) => split?.revert?.());
      splitParagraphs.current = [];

      // Animate Paragraphs with SplitText, one by one
      paragraphRefs.current.forEach((el, i) => {
        const split = new SplitText(el, { type: 'chars' });
        splitParagraphs.current.push(split);
        gsap.fromTo(
          split.chars,
          { opacity: 0, y: 20 },
          {
            opacity: 1,
            y: 0,
            duration: 1.0,
            stagger: 0.02,
            ease: 'power2.out',
            delay: (headingRefs.current.length + i) * 0.1, // continue delay after headings
          }
        );
      });
    }
  }, [isAssistant]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setDropdownOpenTools(false);
        setDropdownOpenFiles(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      {isAssistant &&
        <div className={`chatbot-assitant ${isScrolled ? 'sticky' : ''} ${isExpandChat ? 'expande-chat' : ''}`}>
          <div className='header-chatbox'>
            <div className='left-title-area'>
              <h3>{showHistory ? 'Conversations' : 'New Chat'}</h3>
            </div>
            <div className='right-area'>
              <button className='massage-box' onClick={clearChat}>
                <i className='icon'>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clipPath="url(#clip0_728_2062)">
                      <path d="M6.1602 11.4996C5.17071 11.4985 4.39506 11.3195 3.65387 10.9793C3.57789 10.9445 3.47487 10.9334 3.39371 10.9515C2.62843 11.1213 1.86463 11.2988 1.10158 11.4774C0.910351 11.5222 0.74173 11.4985 0.610169 11.3444C0.478978 11.1909 0.476384 11.0216 0.553097 10.8392C0.802137 10.2485 1.04562 9.65555 1.2954 9.06519C1.33283 8.97625 1.3332 8.90954 1.28169 8.82319C0.623881 7.7203 0.379288 6.52958 0.555321 5.25844C0.879591 2.9148 2.63399 1.06442 4.95873 0.602286C7.83455 0.0308282 10.7189 1.91012 11.3541 4.76963C11.9822 7.59727 10.3854 10.3827 7.63739 11.2502C7.1004 11.4196 6.55044 11.5037 6.1602 11.4996ZM1.69972 10.4112C1.73455 10.4112 1.74864 10.4138 1.76161 10.4108C2.29601 10.287 2.83189 10.1673 3.36443 10.0362C3.55973 9.98797 3.72761 10.0117 3.90846 10.1036C4.85904 10.5865 5.86446 10.7306 6.90917 10.5179C9.39846 10.011 11.0091 7.58948 10.5177 5.10872C9.98733 2.43265 7.24234 0.795736 4.63779 1.60215C1.70157 2.51122 0.46156 5.9963 2.16741 8.55377C2.29823 8.74982 2.32491 8.93104 2.22745 9.14932C2.044 9.56216 1.87797 9.98279 1.69972 10.4112Z" fill="var(--primary-color)" />
                      <path d="M6.44179 5.55974C6.89873 5.55974 7.33752 5.55826 7.77593 5.56048C7.99532 5.56159 8.18396 5.7228 8.21509 5.92848C8.24918 6.15417 8.11465 6.37023 7.89563 6.43064C7.83893 6.4462 7.77704 6.44583 7.71738 6.4462C7.29749 6.44731 6.87761 6.44694 6.44179 6.44694C6.44179 6.56257 6.44179 6.66745 6.44179 6.77233C6.44179 7.10512 6.44327 7.43829 6.44142 7.77108C6.43994 8.03124 6.2513 8.22209 6.00004 8.22284C5.74915 8.22358 5.55681 8.03161 5.55533 7.77404C5.55347 7.38121 5.55458 6.98801 5.55458 6.59481C5.55458 6.54997 5.55458 6.50513 5.55458 6.44694C5.19066 6.44694 4.84045 6.44694 4.49024 6.44694C4.39314 6.44694 4.29604 6.44954 4.19895 6.4462C3.96214 6.43768 3.77758 6.24126 3.77832 6.00149C3.77906 5.76097 3.96325 5.56382 4.20265 5.56159C4.64626 5.55752 5.08986 5.56048 5.55458 5.56048C5.55458 5.50563 5.55458 5.45708 5.55458 5.40854C5.55458 5.02015 5.55347 4.63177 5.55496 4.24339C5.55607 3.97396 5.74433 3.78274 6.00263 3.78459C6.25279 3.78644 6.43994 3.97804 6.44105 4.23968C6.4429 4.67291 6.44179 5.10687 6.44179 5.55974Z" fill="var(--primary-color)" />
                    </g>
                    <defs>
                      <clipPath id="clip0_728_2062">
                        <rect width="12" height="12" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </i>
                <div className='tooltip-chat'>
                  <p>Clear chat</p>
                </div>
              </button>
              <button className={`massage-box ${showHistory && 'active'}`} onClick={() => setShowHistory(!showHistory)}>
                <i className='icon'>
                  <svg width="13" height="12" viewBox="0 0 13 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0.465176 4.68234C0.531938 4.53048 0.627499 4.4166 0.808149 4.40743C0.944291 4.40089 1.05294 4.45194 1.13279 4.56321C1.17468 4.62081 1.21527 4.68103 1.26894 4.75826C1.30035 4.62081 1.32261 4.5043 1.35533 4.38911C1.96143 2.20167 3.36081 0.799675 5.57704 0.323178C7.5799 -0.107501 9.31963 0.488119 10.7321 1.96866C11.5359 2.81169 12.0071 3.83276 12.142 4.99258C12.3553 6.84359 11.782 8.42493 10.4363 9.70911C9.58144 10.5247 8.55122 11.0038 7.37437 11.1399C5.59144 11.3481 4.05461 10.831 2.79661 9.53632C2.64868 9.38316 2.51778 9.21298 2.38818 9.04411C2.2468 8.85953 2.2756 8.637 2.44709 8.50871C2.61726 8.38173 2.8385 8.41969 2.97856 8.6082C3.40139 9.17894 3.92501 9.63057 4.5586 9.95391C5.33094 10.3479 6.15041 10.5037 7.01569 10.4369C8.43602 10.327 9.58799 9.70911 10.4677 8.59249C11.0005 7.91701 11.3303 7.14467 11.4141 6.28724C11.6157 4.23202 10.8237 2.64937 9.0696 1.5694C8.24752 1.0628 7.32856 0.86251 6.36247 0.947598C4.59263 1.10207 3.29274 1.97783 2.45101 3.53429C2.22193 3.95843 2.08055 4.41529 2.02295 4.90618C2.08448 4.83942 2.1447 4.77135 2.20884 4.70721C2.36854 4.55143 2.59239 4.55012 2.74032 4.69935C2.88431 4.84597 2.88431 5.0672 2.73246 5.22167C2.43269 5.52668 2.1303 5.82907 1.82529 6.12753C1.65642 6.29378 1.40639 6.28593 1.27548 6.09612C0.997962 5.69162 0.733533 5.27665 0.463867 4.8656C0.465176 4.80539 0.465176 4.74386 0.465176 4.68234Z" fill="var(--primary-color)" stroke="var(--primary-color)" strokeWidth="0.117857" />
                    <path d="M6.87598 4.09194C6.87598 4.592 6.87729 5.09206 6.87467 5.59212C6.87467 5.65888 6.89299 5.70339 6.94798 5.74397C7.58156 6.22047 8.21383 6.69958 8.84611 7.1787C8.94167 7.2507 9.03068 7.32662 9.05032 7.45622C9.07519 7.61854 9.01367 7.76254 8.88014 7.83846C8.73222 7.92224 8.58822 7.90915 8.45208 7.80705C7.87217 7.37244 7.29226 6.93783 6.71365 6.50192C6.5762 6.3985 6.44006 6.29509 6.3013 6.19298C6.19527 6.11444 6.14291 6.01102 6.14291 5.88012C6.14291 4.77789 6.1416 3.67436 6.14421 2.57213C6.14421 2.36661 6.30523 2.21214 6.50813 2.21083C6.71104 2.21083 6.87336 2.3653 6.87467 2.56951C6.87729 3.07743 6.87598 3.58403 6.87598 4.09194Z" fill="var(--primary-color)" />
                  </svg>
                </i>
                <div className='tooltip-chat'>
                  <p>{showHistory ? 'Hide history' : 'Show history'}</p>
                </div>
              </button>
              {isExpandChat
                ? <button className='massage-box' onClick={() => dispatch(setExpandChat(!isExpandChat))}>
                  <i className='icon'>
                    <svg width="800px" height="800px" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg" fill="var(--primary-color)" stroke="var(--primary-color)" transform="rotate(180)">
                      <g id="SVGRepo_bgCarrier" stroke-width="0" />
                      <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" />
                      <g id="SVGRepo_iconCarrier">
                        <path fill="var(--primary-color)" d="M224 480h640a32 32 0 1 1 0 64H224a32 32 0 0 1 0-64z" />
                        <path fill="var(--primary-color)" d="m237.248 512 265.408 265.344a32 32 0 0 1-45.312 45.312l-288-288a32 32 0 0 1 0-45.312l288-288a32 32 0 1 1 45.312 45.312L237.248 512z" />
                      </g>
                    </svg>
                  </i>
                  <div className='tooltip-chat'>
                    <p>Closed</p>
                  </div>
                </button>
                : <button className='massage-box' onClick={() => dispatch(setExpandChat(!isExpandChat))}>
                  <i className='icon'>
                    <svg width="800px" height="800px" viewBox="-2.4 -2.4 28.80 28.80" fill="none" xmlns="http://www.w3.org/2000/svg" transform="rotate(0)">
                      <g id="SVGRepo_bgCarrier" stroke-width="0" />
                      <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" stroke="#CCCCCC" stroke-width="0.048" />
                      <g id="SVGRepo_iconCarrier"> <path d="M14 10L21 3M21 3H16.5M21 3V7.5M10 14L3 21M3 21H7.5M3 21L3 16.5" stroke="var(--primary-color)" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" /> </g>
                    </svg>
                  </i>
                  <div className='tooltip-chat'>
                    <p>Expand</p>
                  </div>
                </button>
              }
              <button className='massage-box close-btn' onClick={() => { dispatch(setAssistant(!isAssistant)); dispatch(setExpandChat(false)); }}>
                <i className='icon'>x</i>
                <div className='tooltip-chat'>
                  <p>Close</p>
                </div>
              </button>
            </div>
          </div>
          {showHistory
            ? <ThreadHistory setShowHistory={setShowHistory} onNewChat={clearChat} onSelectThread={(selectedThreadId) => {
              // Mark that we should clear messages when switching to this thread
              shouldClearMessagesRef.current = true;
              
              // Clear current chat messages first
              setMessages([]);

              // Update Redux state with selected thread
              dispatch(setCurrentThread(selectedThreadId));
              // The useEffect will handle updating local threadId
            }}
            />
            : <div className='center-box-chat'>
              {displayMessages.length === 0
                ? <div className='main-screeen'>
                  <div className='img-box'>
                    <img alt="..." src="https://assets-seleagent.s3.us-east-2.amazonaws.com/aiassistant-help.gif" />
                  </div>
                  <div className='text-box'>

                    {/* <h3></h3> */}
                    <h3 ref={(el) => setParagraphRef(el, 0)}>Hi, I'm Milo.</h3>
                    <p ref={(el) => setHeadingRef(el, 0)} >I'm your AI marketing assistant with MCP tool capabilities. Ask me anything!</p>
                    <div className='prompet-box'>
                      <ul>
                        <li ref={(el) => setHeadingRef(el, 1)}>
                          <button onClick={() => handlePromptClick("Analyze all calls made in past couple of days")}>
                            <i className='icon-left'>
                              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M3.33398 2.39581C3.16315 2.39581 3.02148 2.25415 3.02148 2.08331V0.833313C3.02148 0.66248 3.16315 0.520813 3.33398 0.520813C3.50482 0.520813 3.64648 0.66248 3.64648 0.833313V2.08331C3.64648 2.25415 3.50482 2.39581 3.33398 2.39581Z" fill="#4B5563" />
                                <path d="M6.66602 2.39581C6.49518 2.39581 6.35352 2.25415 6.35352 2.08331V0.833313C6.35352 0.66248 6.49518 0.520813 6.66602 0.520813C6.83685 0.520813 6.97852 0.66248 6.97852 0.833313V2.08331C6.97852 2.25415 6.83685 2.39581 6.66602 2.39581Z" fill="#4B5563" />
                                <path d="M8.54232 4.09998H1.45898C1.28815 4.09998 1.14648 3.95831 1.14648 3.78748C1.14648 3.61664 1.28815 3.47498 1.45898 3.47498H8.54232C8.71315 3.47498 8.85482 3.61664 8.85482 3.78748C8.85482 3.95831 8.71315 4.09998 8.54232 4.09998Z" fill="#4B5563" />
                                <path d="M6.66667 9.47915H3.33333C1.8125 9.47915 0.9375 8.60415 0.9375 7.08331V3.54165C0.9375 2.02081 1.8125 1.14581 3.33333 1.14581H6.66667C8.1875 1.14581 9.0625 2.02081 9.0625 3.54165V7.08331C9.0625 8.60415 8.1875 9.47915 6.66667 9.47915ZM3.33333 1.77081C2.14167 1.77081 1.5625 2.34998 1.5625 3.54165V7.08331C1.5625 8.27498 2.14167 8.85415 3.33333 8.85415H6.66667C7.85833 8.85415 8.4375 8.27498 8.4375 7.08331V3.54165C8.4375 2.34998 7.85833 1.77081 6.66667 1.77081H3.33333Z" fill="#4B5563" />
                                <path d="M3.54167 6.04165C3.4875 6.04165 3.43333 6.02916 3.38333 6.00832C3.33333 5.98749 3.2875 5.95831 3.24584 5.92081C3.20834 5.87915 3.17916 5.83332 3.15833 5.78332C3.13749 5.73332 3.125 5.67915 3.125 5.62498C3.125 5.51665 3.17084 5.40832 3.24584 5.32915C3.2875 5.29165 3.33333 5.26248 3.38333 5.24165C3.45833 5.20831 3.54167 5.19998 3.62501 5.21665C3.65001 5.22081 3.675 5.22915 3.7 5.24165C3.725 5.24998 3.75 5.26249 3.775 5.27916C3.79583 5.29582 3.81666 5.31249 3.8375 5.32915C3.85416 5.34999 3.87499 5.37082 3.88749 5.39165C3.90416 5.41665 3.91667 5.44165 3.92501 5.46665C3.93751 5.49165 3.94584 5.51665 3.95 5.54165C3.95417 5.57081 3.95833 5.59582 3.95833 5.62498C3.95833 5.73332 3.9125 5.84165 3.8375 5.92081C3.75833 5.99581 3.65 6.04165 3.54167 6.04165Z" fill="#4B5563" />
                                <path d="M5.00065 6.04167C4.89232 6.04167 4.78399 5.99584 4.70482 5.92084C4.68815 5.9 4.67149 5.87917 4.65482 5.85834C4.63816 5.83334 4.62565 5.80834 4.61731 5.78334C4.60481 5.75834 4.59648 5.73335 4.59231 5.70835C4.58815 5.67918 4.58398 5.65417 4.58398 5.62501C4.58398 5.57084 4.59648 5.51667 4.61731 5.46667C4.63815 5.41667 4.66732 5.37085 4.70482 5.32918C4.82149 5.21251 5.00899 5.175 5.15899 5.24167C5.21315 5.2625 5.25481 5.29168 5.29648 5.32918C5.37148 5.40835 5.41732 5.51667 5.41732 5.62501C5.41732 5.65417 5.41316 5.67918 5.40899 5.70835C5.40482 5.73335 5.39649 5.75834 5.38399 5.78334C5.37566 5.80834 5.36315 5.83334 5.34648 5.85834C5.32981 5.87917 5.31315 5.9 5.29648 5.92084C5.25481 5.95834 5.21315 5.98751 5.15899 6.00835C5.10899 6.02918 5.05482 6.04167 5.00065 6.04167Z" fill="#4B5563" />
                                <path d="M3.54167 7.49999C3.4875 7.49999 3.43333 7.48749 3.38333 7.46666C3.33333 7.44583 3.2875 7.41665 3.24584 7.37915C3.20834 7.33748 3.17916 7.29582 3.15833 7.24166C3.13749 7.19166 3.125 7.13749 3.125 7.08332C3.125 6.97499 3.17084 6.86666 3.24584 6.78749C3.2875 6.74999 3.33333 6.72082 3.38333 6.69998C3.5375 6.63332 3.72083 6.67082 3.8375 6.78749C3.85416 6.80832 3.87499 6.82916 3.88749 6.84999C3.90416 6.87499 3.91667 6.89999 3.92501 6.92499C3.93751 6.94999 3.94584 6.97499 3.95 7.00415C3.95417 7.02915 3.95833 7.05832 3.95833 7.08332C3.95833 7.19165 3.9125 7.29998 3.8375 7.37915C3.75833 7.45415 3.65 7.49999 3.54167 7.49999Z" fill="#4B5563" />
                              </svg>
                            </i>
                            <span>Analyze all calls made in past couple of days</span>
                          </button>
                        </li>
                        <li ref={(el) => setHeadingRef(el, 2)}>
                          <button onClick={() => handlePromptClick("Can you make a demo call for me?")}>
                            <i className='icon-left'>
                              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.0875 17.0625C12.24 17.0625 11.3475 16.86 10.425 16.47C9.525 16.0875 8.6175 15.5625 7.7325 14.925C6.855 14.28 6.0075 13.56 5.205 12.7725C4.41 11.97 3.69 11.1225 3.0525 10.2525C2.4075 9.3525 1.89 8.4525 1.5225 7.5825C1.1325 6.6525 0.9375 5.7525 0.9375 4.905C0.9375 4.32 1.0425 3.765 1.245 3.2475C1.455 2.715 1.7925 2.22 2.25 1.7925C2.8275 1.2225 3.4875 0.9375 4.1925 0.9375C4.485 0.9375 4.785 1.005 5.04 1.125C5.3325 1.26 5.58 1.4625 5.76 1.7325L7.5 4.185C7.6575 4.4025 7.7775 4.6125 7.86 4.8225C7.9575 5.0475 8.01 5.2725 8.01 5.49C8.01 5.775 7.9275 6.0525 7.77 6.315C7.6575 6.5175 7.485 6.735 7.2675 6.9525L6.7575 7.485C6.765 7.5075 6.7725 7.5225 6.78 7.5375C6.87 7.695 7.05 7.965 7.395 8.37C7.7625 8.79 8.1075 9.1725 8.4525 9.525C8.895 9.96 9.2625 10.305 9.6075 10.59C10.035 10.95 10.3125 11.13 10.4775 11.2125L10.4625 11.25L11.01 10.71C11.2425 10.4775 11.4675 10.305 11.685 10.1925C12.0975 9.9375 12.6225 9.8925 13.1475 10.11C13.3425 10.1925 13.5525 10.305 13.7775 10.4625L16.2675 12.2325C16.545 12.42 16.7475 12.66 16.8675 12.945C16.98 13.23 17.0325 13.4925 17.0325 13.755C17.0325 14.115 16.95 14.475 16.7925 14.8125C16.635 15.15 16.44 15.4425 16.1925 15.7125C15.765 16.185 15.3 16.5225 14.76 16.74C14.2425 16.95 13.68 17.0625 13.0875 17.0625ZM4.1925 2.0625C3.78 2.0625 3.3975 2.2425 3.03 2.6025C2.685 2.925 2.445 3.2775 2.295 3.66C2.1375 4.05 2.0625 4.4625 2.0625 4.905C2.0625 5.6025 2.2275 6.36 2.5575 7.14C2.895 7.935 3.3675 8.76 3.9675 9.585C4.5675 10.41 5.25 11.2125 6 11.97C6.75 12.7125 7.56 13.4025 8.3925 14.01C9.2025 14.6025 10.035 15.0825 10.86 15.4275C12.1425 15.975 13.3425 16.1025 14.3325 15.69C14.715 15.5325 15.0525 15.2925 15.36 14.9475C15.5325 14.76 15.6675 14.5575 15.78 14.3175C15.87 14.13 15.915 13.935 15.915 13.74C15.915 13.62 15.8925 13.5 15.8325 13.365C15.81 13.32 15.765 13.2375 15.6225 13.14L13.1325 11.37C12.9825 11.265 12.8475 11.19 12.72 11.1375C12.555 11.07 12.4875 11.0025 12.2325 11.16C12.0825 11.235 11.9475 11.3475 11.7975 11.4975L11.2275 12.06C10.935 12.345 10.485 12.4125 10.14 12.285L9.9375 12.195C9.63 12.03 9.27 11.775 8.8725 11.4375C8.5125 11.13 8.1225 10.77 7.65 10.305C7.2825 9.93 6.915 9.5325 6.5325 9.09C6.18 8.6775 5.925 8.325 5.7675 8.0325L5.6775 7.8075C5.6325 7.635 5.6175 7.5375 5.6175 7.4325C5.6175 7.1625 5.715 6.9225 5.9025 6.735L6.465 6.15C6.615 6 6.7275 5.8575 6.8025 5.73C6.8625 5.6325 6.885 5.55 6.885 5.475C6.885 5.415 6.8625 5.325 6.825 5.235C6.7725 5.115 6.69 4.98 6.585 4.8375L4.845 2.3775C4.77 2.2725 4.68 2.1975 4.5675 2.145C4.4475 2.0925 4.32 2.0625 4.1925 2.0625ZM10.4625 11.2575L10.3425 11.7675L10.545 11.2425C10.5075 11.235 10.4775 11.2425 10.4625 11.2575Z" fill="currentColor"></path></svg>
                            </i>
                            <span>Can you make a demo call for me?</span>
                          </button>
                        </li>
                        <li ref={(el) => setHeadingRef(el, 3)}>
                          <button onClick={() => handlePromptClick("Can you improve agent prompt? I am facing some issues")}>
                            <i className='icon-left'>
                              <svg width="23" height="20" viewBox="0 0 23 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20.0784 14.7194C19.9785 15.789 19.641 16.7644 19.0031 17.6185C17.8657 19.1404 16.3431 19.9588 14.4394 19.9824C12.2146 20.0095 9.98909 20.0009 7.7636 19.9859C4.84741 19.9667 2.43497 17.754 2.13101 14.8471C2.12673 14.8043 2.1146 14.7622 2.10746 14.7244C0.792433 14.4632 0.069629 13.7683 0.00469798 12.7008C-0.033119 12.0851 0.157393 11.5442 0.57552 11.0875C0.986512 10.6387 1.51381 10.4325 2.11317 10.3854C2.20522 9.3501 2.51917 8.41252 3.10997 7.58412C4.26374 5.96726 5.84635 5.14029 7.83138 5.10318C8.66764 5.08748 9.5046 5.10033 10.3416 5.10033C10.4386 5.10033 10.5357 5.10033 10.6719 5.10033C10.6719 4.81777 10.6719 4.55377 10.6719 4.28976C10.6719 3.85379 10.6648 3.41712 10.6755 2.98115C10.6798 2.81918 10.6355 2.73641 10.4793 2.66006C9.86636 2.36181 9.56882 1.70108 9.73079 1.05177C9.88634 0.429575 10.4536 -0.00424976 11.1065 3.14058e-05C11.7679 0.00431257 12.3152 0.447413 12.4679 1.10172C12.612 1.71963 12.3045 2.35253 11.7165 2.66648C11.6416 2.70644 11.5824 2.78279 11.531 2.85343C11.5096 2.88268 11.5267 2.93977 11.5267 2.98472C11.5267 3.67755 11.5267 4.36968 11.5267 5.09961C11.6509 5.09961 11.7544 5.0989 11.8571 5.09961C12.8803 5.11103 13.9064 5.07749 14.926 5.14243C17.4697 5.30582 19.7166 7.46853 20.0291 10.0015C20.0441 10.1243 20.0641 10.247 20.0812 10.3633C21.5818 10.7729 22.2411 11.4878 22.1826 12.6323C22.1269 13.7661 21.382 14.5125 20.0784 14.7194ZM11.0929 19.1347C12.1696 19.1347 13.2463 19.1404 14.3231 19.1333C17.1001 19.1154 19.22 16.9948 19.2328 14.2242C19.2378 13.1033 19.24 11.9816 19.2293 10.8606C19.2264 10.5331 19.205 10.1999 19.1387 9.88025C18.6563 7.54131 16.707 5.96441 14.2888 5.95513C12.251 5.94729 10.2131 5.95228 8.1753 5.95513C7.88204 5.95585 7.58807 5.96798 7.29624 5.99081C5.13568 6.16277 3.1592 8.05362 3.00936 10.2163C2.90947 11.6512 2.97083 13.0968 2.96512 14.5382C2.96441 14.7058 2.98796 14.8757 3.01864 15.0419C3.46388 17.49 5.42109 19.1261 7.91629 19.134C8.97445 19.1383 10.0333 19.1347 11.0929 19.1347ZM20.1091 11.2909C20.1091 12.1357 20.1091 12.9812 20.1091 13.8275C20.8604 13.7775 21.3884 13.1696 21.3278 12.4447C21.2728 11.7775 20.7056 11.2381 20.1091 11.2909ZM2.07036 13.8303C2.07036 12.967 2.07036 12.1214 2.07036 11.2752C1.39679 11.3016 0.872348 11.8446 0.855223 12.5103C0.838098 13.2046 1.34399 13.7618 2.07036 13.8303ZM11.0908 0.856978C10.7897 0.859119 10.5528 1.09387 10.5492 1.39426C10.5456 1.70536 10.7904 1.94939 11.1029 1.94582C11.4054 1.94297 11.6395 1.70964 11.6438 1.40854C11.6473 1.09744 11.4047 0.854838 11.0908 0.856978Z" fill="#3A3D44"></path><path d="M11.0858 16.6408C9.91135 16.6408 8.73688 16.6429 7.56241 16.6401C5.78644 16.6365 4.59913 15.4499 4.59699 13.6782C4.59628 12.904 4.59414 12.1298 4.59771 11.3557C4.60484 9.65747 5.81498 8.45161 7.51389 8.4509C9.8985 8.44947 12.2831 8.44947 14.6684 8.4509C16.3773 8.45161 17.5753 9.63678 17.5868 11.3464C17.5925 12.2362 17.6046 13.1266 17.5739 14.0157C17.5268 15.3999 16.3638 16.5837 14.9845 16.6222C13.6866 16.6586 12.3866 16.6301 11.0872 16.6301C11.0858 16.6336 11.0858 16.6372 11.0858 16.6408ZM11.0801 15.786C12.2902 15.786 13.4997 15.7917 14.7091 15.7845C15.9071 15.7767 16.7184 14.969 16.7298 13.776C16.7377 12.9575 16.737 12.1391 16.7298 11.3207C16.7191 10.117 15.8936 9.30642 14.6856 9.30571C12.2924 9.30428 9.89993 9.30428 7.50676 9.30571C6.2802 9.30642 5.45537 10.1363 5.4518 11.3707C5.44966 12.1356 5.45109 12.9005 5.4518 13.6654C5.45251 14.9754 6.25309 15.7824 7.55813 15.7845C8.73189 15.7874 9.90564 15.786 11.0801 15.786Z" fill="#3A3D44"></path><path d="M8.341 14.3347C7.35419 14.3305 6.5636 13.5363 6.5636 12.5488C6.5636 11.552 7.3706 10.7457 8.36312 10.7521C9.34208 10.7585 10.1441 11.5648 10.1455 12.5438C10.1462 13.5384 9.33994 14.3383 8.341 14.3347ZM8.31745 13.4785C8.8476 13.4928 9.28214 13.0832 9.28999 12.5609C9.29784 12.0507 8.8847 11.6212 8.37382 11.6076C7.86507 11.5941 7.43054 12.0093 7.41912 12.5188C7.4077 13.0411 7.80371 13.4642 8.31745 13.4785Z" fill="#3A3D44"></path><path d="M13.846 14.3347C12.8464 14.3383 12.0465 13.5384 12.0508 12.5402C12.0551 11.5555 12.8521 10.7557 13.8325 10.7521C14.8193 10.7485 15.6406 11.5691 15.6348 12.553C15.6299 13.5356 14.8307 14.3311 13.846 14.3347ZM13.8418 13.4785C14.3562 13.477 14.7765 13.0611 14.7786 12.5502C14.7808 12.035 14.3384 11.5976 13.8225 11.6076C13.3109 11.6169 12.902 12.0407 12.9063 12.5559C12.9113 13.076 13.3209 13.4799 13.8418 13.4785Z" fill="currentColor"></path></svg>
                            </i>
                            <span>Can you improve agent prompt? I am facing some issues</span>
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                : <div className='main-screeen chat-main-screen'>
                  <div className='chat-messages'>
                    {displayMessages.map((msg, index) => (
                      <>
                        <div
                          key={index}
                          style={{ alignSelf: msg['role'] === 'user' ? 'flex-end' : 'flex-start', marginLeft: msg['role'] === 'user' ? 'auto' : '0', marginRight: msg['role'] === 'user' ? '0' : 'auto' }}
                          className={`message ${msg['role']}`}>
                          {msg['role'] === 'user'
                            ?
                            <>
                              {userDetails && userDetails['profilePicture'] ?
                                <div className='icon-ai img-show'><img src={userDetails?.['profilePicture']} alt='Profile' /></div>
                                :
                                <div className='icon-ai'>
                                  <span className='text-name'>{userDetails?.['firstName']?.charAt(0) || 'X'}</span>
                                </div>
                              }
                              {/* <div className='icon-ai'><img src={require('../../static/img/agent-1.jpeg')} alt="" /></div> */}
                            </>
                            : <div className='icon-ai'><img alt="..." src="https://assets-seleagent.s3.us-east-2.amazonaws.com/aiassistant-help.gif" /></div>
                          }
                          <div className='chat-box-color' style={{ backgroundColor: msg['role'] === 'user' ? '#F3F2FF' : '#FBFCFF' }}>
                            {/* Show user uploads */}
                            {msg['role'] === 'user' && msg['experimental_attachments'] && (

                              <div className="user-attachments">
                                {msg['experimental_attachments']
                                  .filter(file => file?.contentType?.startsWith?.('image/') && file.data)
                                  .map((file, idx) => (
                                    <div key={idx} className="attachment-preview">
                                      <img
                                        src={file.data}
                                        alt={file.name || 'Uploaded image'}
                                        style={{ maxWidth: '200px', maxHeight: '200px' }}
                                        onError={(e) => {
                                          e.target.onerror = null;
                                          e.target.style.display = 'none';
                                        }}
                                      />
                                      {/* {file.name && <div className="file-name">{file.name}</div>} */}
                                    </div>
                                  ))
                                }
                              </div>
                            )}
                            {formatMessage(msg)}
                          </div>
                          <div class="copy-btn-bottom">
                            <div class="button-options">
                              {/* <CopyToClipboard text={msg} onCopy={() => EventBus.publish('success', 'Message copied!')}> */}
                              <CopyToClipboard text={msg['content']} onCopy={handleCopy}>
                                <button>
                                  <span>
                                    {copied ? (
                                      <svg width="20" height="20" viewBox="0 0 20 20" fill="#01A601" xmlns="http://www.w3.org/2000/svg"><path d="M7.629 13.71L4.292 10.374L3.165 11.5L7.629 15.964L16.834 6.758L15.707 5.632L7.629 13.71Z" /></svg>
                                    ) : (
                                      <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg" class="icon"><path d="M12.668 10.667C12.668 9.95614 12.668 9.46258 12.6367 9.0791C12.6137 8.79732 12.5758 8.60761 12.5244 8.46387L12.4688 8.33399C12.3148 8.03193 12.0803 7.77885 11.793 7.60254L11.666 7.53125C11.508 7.45087 11.2963 7.39395 10.9209 7.36328C10.5374 7.33197 10.0439 7.33203 9.33301 7.33203H6.5C5.78896 7.33203 5.29563 7.33195 4.91211 7.36328C4.63016 7.38632 4.44065 7.42413 4.29688 7.47559L4.16699 7.53125C3.86488 7.68518 3.61186 7.9196 3.43555 8.20703L3.36524 8.33399C3.28478 8.49198 3.22795 8.70352 3.19727 9.0791C3.16595 9.46259 3.16504 9.95611 3.16504 10.667V13.5C3.16504 14.211 3.16593 14.7044 3.19727 15.0879C3.22797 15.4636 3.28473 15.675 3.36524 15.833L3.43555 15.959C3.61186 16.2466 3.86474 16.4807 4.16699 16.6348L4.29688 16.6914C4.44063 16.7428 4.63025 16.7797 4.91211 16.8027C5.29563 16.8341 5.78896 16.835 6.5 16.835H9.33301C10.0439 16.835 10.5374 16.8341 10.9209 16.8027C11.2965 16.772 11.508 16.7152 11.666 16.6348L11.793 16.5645C12.0804 16.3881 12.3148 16.1351 12.4688 15.833L12.5244 15.7031C12.5759 15.5594 12.6137 15.3698 12.6367 15.0879C12.6681 14.7044 12.668 14.211 12.668 13.5V10.667ZM13.998 12.665C14.4528 12.6634 14.8011 12.6602 15.0879 12.6367C15.4635 12.606 15.675 12.5492 15.833 12.4688L15.959 12.3975C16.2466 12.2211 16.4808 11.9682 16.6348 11.666L16.6914 11.5361C16.7428 11.3924 16.7797 11.2026 16.8027 10.9209C16.8341 10.5374 16.835 10.0439 16.835 9.33301V6.5C16.835 5.78896 16.8341 5.29563 16.8027 4.91211C16.7797 4.63025 16.7428 4.44063 16.6914 4.29688L16.6348 4.16699C16.4807 3.86474 16.2466 3.61186 15.959 3.43555L15.833 3.36524C15.675 3.28473 15.4636 3.22797 15.0879 3.19727C14.7044 3.16593 14.211 3.16504 13.5 3.16504H10.667C9.9561 3.16504 9.46259 3.16595 9.0791 3.19727C8.79739 3.22028 8.6076 3.2572 8.46387 3.30859L8.33399 3.36524C8.03176 3.51923 7.77886 3.75343 7.60254 4.04102L7.53125 4.16699C7.4508 4.32498 7.39397 4.53655 7.36328 4.91211C7.33985 5.19893 7.33562 5.54719 7.33399 6.00195H9.33301C10.022 6.00195 10.5791 6.00131 11.0293 6.03809C11.4873 6.07551 11.8937 6.15471 12.2705 6.34668L12.4883 6.46875C12.984 6.7728 13.3878 7.20854 13.6533 7.72949L13.7197 7.87207C13.8642 8.20859 13.9292 8.56974 13.9619 8.9707C13.9987 9.42092 13.998 9.97799 13.998 10.667V12.665ZM18.165 9.33301C18.165 10.022 18.1657 10.5791 18.1289 11.0293C18.0961 11.4302 18.0311 11.7914 17.8867 12.1279L17.8203 12.2705C17.5549 12.7914 17.1509 13.2272 16.6553 13.5313L16.4365 13.6533C16.0599 13.8452 15.6541 13.9245 15.1963 13.9619C14.8593 13.9895 14.4624 13.9935 13.9951 13.9951C13.9935 14.4624 13.9895 14.8593 13.9619 15.1963C13.9292 15.597 13.864 15.9576 13.7197 16.2939L13.6533 16.4365C13.3878 16.9576 12.9841 17.3941 12.4883 17.6982L12.2705 17.8203C11.8937 18.0123 11.4873 18.0915 11.0293 18.1289C10.5791 18.1657 10.022 18.165 9.33301 18.165H6.5C5.81091 18.165 5.25395 18.1657 4.80371 18.1289C4.40306 18.0962 4.04235 18.031 3.70606 17.8867L3.56348 17.8203C3.04244 17.5548 2.60585 17.151 2.30176 16.6553L2.17969 16.4365C1.98788 16.0599 1.90851 15.6541 1.87109 15.1963C1.83431 14.746 1.83496 14.1891 1.83496 13.5V10.667C1.83496 9.978 1.83432 9.42091 1.87109 8.9707C1.90851 8.5127 1.98772 8.10625 2.17969 7.72949L2.30176 7.51172C2.60586 7.0159 3.04236 6.6122 3.56348 6.34668L3.70606 6.28027C4.04237 6.136 4.40303 6.07083 4.80371 6.03809C5.14051 6.01057 5.53708 6.00551 6.00391 6.00391C6.00551 5.53708 6.01057 5.14051 6.03809 4.80371C6.0755 4.34588 6.15483 3.94012 6.34668 3.56348L6.46875 3.34473C6.77282 2.84912 7.20856 2.44514 7.72949 2.17969L7.87207 2.11328C8.20855 1.96886 8.56979 1.90385 8.9707 1.87109C9.42091 1.83432 9.978 1.83496 10.667 1.83496H13.5C14.1891 1.83496 14.746 1.83431 15.1963 1.87109C15.6541 1.90851 16.0599 1.98788 16.4365 2.17969L16.6553 2.30176C17.151 2.60585 17.5548 3.04244 17.8203 3.56348L17.8867 3.70606C18.031 4.04235 18.0962 4.40306 18.1289 4.80371C18.1657 5.25395 18.165 5.81091 18.165 6.5V9.33301Z"></path></svg>
                                    )}
                                  </span>
                                </button>
                              </CopyToClipboard>
                              {msg['role'] === 'assistant' && (
                                <>
                                  <button

                                  >
                                    <span><svg width="20" height="20" viewBox="0 0 20 20" fill={msg.feedback === 'like' ? '#4CAF50' : 'currentColor'} xmlns="http://www.w3.org/2000/svg" class="icon"><path d="M10.9153 1.83987L11.2942 1.88772L11.4749 1.91507C13.2633 2.24201 14.4107 4.01717 13.9749 5.78225L13.9261 5.95901L13.3987 7.6719C13.7708 7.67575 14.0961 7.68389 14.3792 7.70608C14.8737 7.74486 15.3109 7.82759 15.7015 8.03323L15.8528 8.11819C16.5966 8.56353 17.1278 9.29625 17.3167 10.1475L17.347 10.3096C17.403 10.69 17.3647 11.0832 17.2835 11.5098C17.2375 11.7517 17.1735 12.0212 17.096 12.3233L16.8255 13.3321L16.4456 14.7276C16.2076 15.6001 16.0438 16.2356 15.7366 16.7305L15.595 16.9346C15.2989 17.318 14.9197 17.628 14.4866 17.8408L14.2982 17.9258C13.6885 18.1774 12.9785 18.1651 11.9446 18.1651H7.33331C6.64422 18.1651 6.08726 18.1657 5.63702 18.1289C5.23638 18.0962 4.87565 18.031 4.53936 17.8867L4.39679 17.8203C3.87576 17.5549 3.43916 17.151 3.13507 16.6553L3.013 16.4366C2.82119 16.0599 2.74182 15.6541 2.7044 15.1963C2.66762 14.7461 2.66827 14.1891 2.66827 13.5V11.667C2.66827 10.9349 2.66214 10.4375 2.77569 10.0137L2.83722 9.81253C3.17599 8.81768 3.99001 8.05084 5.01397 7.77639L5.17706 7.73928C5.56592 7.66435 6.02595 7.66799 6.66632 7.66799C6.9429 7.66799 7.19894 7.52038 7.33624 7.2803L10.2562 2.16995L10.3118 2.08792C10.4544 1.90739 10.6824 1.81092 10.9153 1.83987ZM7.33136 14.167C7.33136 14.9841 7.33714 15.2627 7.39386 15.4746L7.42999 15.5918C7.62644 16.1686 8.09802 16.6134 8.69171 16.7725L8.87042 16.8067C9.07652 16.8323 9.38687 16.835 10.0003 16.835H11.9446C13.099 16.835 13.4838 16.8228 13.7903 16.6963L13.8997 16.6465C14.1508 16.5231 14.3716 16.3444 14.5433 16.1221L14.6155 16.0166C14.7769 15.7552 14.8968 15.3517 15.1624 14.378L15.5433 12.9824L15.8079 11.9922C15.8804 11.7102 15.9368 11.4711 15.9769 11.2608C16.0364 10.948 16.0517 10.7375 16.0394 10.5791L16.0179 10.4356C15.9156 9.97497 15.641 9.57381 15.2542 9.31253L15.0814 9.20999C14.9253 9.12785 14.6982 9.06544 14.2747 9.03225C13.8477 8.99881 13.2923 8.99807 12.5003 8.99807C12.2893 8.99807 12.0905 8.89822 11.9651 8.72854C11.8398 8.55879 11.8025 8.33942 11.8646 8.13772L12.6556 5.56741L12.7054 5.36331C12.8941 4.35953 12.216 3.37956 11.1878 3.2178L8.49054 7.93948C8.23033 8.39484 7.81431 8.72848 7.33136 8.88967V14.167ZM3.99835 13.5C3.99835 14.2111 3.99924 14.7044 4.03058 15.0879C4.06128 15.4636 4.11804 15.675 4.19854 15.833L4.26886 15.959C4.44517 16.2466 4.69805 16.4808 5.0003 16.6348L5.13019 16.6905C5.27397 16.7419 5.46337 16.7797 5.74542 16.8028C5.97772 16.8217 6.25037 16.828 6.58722 16.8311C6.41249 16.585 6.27075 16.3136 6.1712 16.0215L6.10968 15.8194C5.99614 15.3956 6.00128 14.899 6.00128 14.167V9.00296C5.79386 9.0067 5.65011 9.01339 5.53741 9.02737L5.3587 9.06057C4.76502 9.21965 4.29247 9.66448 4.09601 10.2412L4.06085 10.3584C4.00404 10.5705 3.99835 10.8493 3.99835 11.667V13.5Z"></path></svg></span>
                                  </button>
                                  <button

                                  >
                                    <span><svg width="20" height="20" viewBox="0 0 20 20" fill={msg.feedback === 'dislike' ? '#f44336' : 'currentColor'} xmlns="http://www.w3.org/2000/svg" class="icon"><path d="M12.6687 5.83304C12.6687 5.22006 12.6649 4.91019 12.6394 4.70413L12.6062 4.52542C12.4471 3.93179 12.0022 3.45922 11.4255 3.26272L11.3083 3.22757C11.0963 3.17075 10.8175 3.16507 9.99974 3.16507H8.0554C7.04558 3.16507 6.62456 3.17475 6.32982 3.26175L6.2097 3.30374C5.95005 3.41089 5.71908 3.57635 5.53392 3.78616L5.45677 3.87796C5.30475 4.0748 5.20336 4.33135 5.03392 4.91702L4.83763 5.6221L4.45677 7.01761C4.24829 7.78204 4.10326 8.31846 4.02318 8.73929C3.94374 9.15672 3.94298 9.39229 3.98119 9.56448L4.03587 9.75784C4.18618 10.1996 4.50043 10.5702 4.91771 10.7901L5.05052 10.8477C5.20009 10.9014 5.40751 10.9429 5.72533 10.9678C6.15231 11.0012 6.70771 11.002 7.49974 11.002C7.71076 11.002 7.90952 11.1018 8.0349 11.2715C8.14465 11.4201 8.18683 11.6067 8.15404 11.7862L8.13548 11.8623L7.34447 14.4326C7.01523 15.5033 7.71404 16.6081 8.81126 16.7813L11.5095 12.0606L11.5827 11.9405C11.8445 11.5461 12.2289 11.2561 12.6687 11.1094V5.83304ZM17.3318 8.33304C17.3318 8.97366 17.3364 9.43432 17.2615 9.82327L17.2234 9.98538C16.949 11.0094 16.1821 11.8233 15.1872 12.1621L14.9861 12.2237C14.5624 12.3372 14.0656 12.3321 13.3337 12.3321C13.0915 12.3321 12.8651 12.4453 12.7204 12.6348L12.6638 12.7198L9.74388 17.8301C9.61066 18.0631 9.35005 18.1935 9.08372 18.1602L8.70579 18.1123C6.75379 17.8682 5.49542 15.9213 6.07396 14.041L6.60033 12.3272C6.22861 12.3233 5.90377 12.3161 5.62083 12.294C5.18804 12.26 4.79914 12.1931 4.44701 12.0391L4.29857 11.9668C3.52688 11.5605 2.95919 10.8555 2.72533 10.0205L2.68333 9.85257C2.58769 9.42154 2.62379 8.97768 2.71654 8.49026C2.80865 8.00634 2.97082 7.41139 3.17357 6.668L3.55443 5.27249L3.74583 4.58011C3.9286 3.94171 4.10186 3.45682 4.40404 3.06546L4.53685 2.9053C4.85609 2.54372 5.25433 2.25896 5.70189 2.07425L5.93626 1.99222C6.49455 1.82612 7.15095 1.83499 8.0554 1.83499H12.6667C13.3558 1.83499 13.9128 1.83434 14.363 1.87112C14.8208 1.90854 15.2266 1.98789 15.6033 2.17972L15.821 2.30179C16.317 2.6059 16.7215 3.04226 16.987 3.56351L17.0535 3.70608C17.1977 4.04236 17.2629 4.40311 17.2956 4.80374C17.3324 5.25398 17.3318 5.81094 17.3318 6.50003V8.33304ZM13.9978 10.9961C14.3321 10.9901 14.5013 10.977 14.6413 10.9395L14.7585 10.9033C15.3353 10.7069 15.7801 10.2353 15.9392 9.64163L15.9724 9.46292C15.998 9.25682 16.0017 8.94657 16.0017 8.33304V6.50003C16.0017 5.78899 16.0008 5.29566 15.9695 4.91214C15.9464 4.6301 15.9086 4.44069 15.8572 4.2969L15.8015 4.16702C15.6475 3.86478 15.4133 3.6119 15.1257 3.43558L14.9997 3.36526C14.8418 3.28477 14.6302 3.228 14.2546 3.19729C14.0221 3.1783 13.7491 3.17109 13.4118 3.168C13.6267 3.47028 13.7914 3.81126 13.8904 4.18069L13.9275 4.34378C13.981 4.62163 13.9947 4.93582 13.9978 5.3262V10.9961Z"></path></svg></span>
                                  </button>
                                </>
                              )}
                              <button onClick={() => handleSpeak(msg['content'])}>
                                <span><svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg" class="icon"><path d="M9.75122 4.09203C9.75122 3.61482 9.21964 3.35044 8.84399 3.60277L8.77173 3.66039L6.55396 5.69262C6.05931 6.14604 5.43173 6.42255 4.7688 6.48461L4.48267 6.49828C3.52474 6.49851 2.74829 7.27565 2.74829 8.23363V11.7668C2.74829 12.7248 3.52474 13.501 4.48267 13.5012C5.24935 13.5012 5.98874 13.7889 6.55396 14.3069L8.77173 16.3401L8.84399 16.3967C9.21966 16.6493 9.75122 16.3858 9.75122 15.9084V4.09203ZM17.2483 10.0002C17.2483 8.67623 16.9128 7.43233 16.3235 6.34691L17.4924 5.71215C18.1849 6.9875 18.5784 8.4491 18.5784 10.0002C18.5783 11.5143 18.2033 12.9429 17.5413 14.1965C17.3697 14.5212 16.9675 14.6453 16.6428 14.4739C16.3182 14.3023 16.194 13.9001 16.3655 13.5754C16.9288 12.5086 17.2483 11.2927 17.2483 10.0002ZM13.9182 10.0002C13.9182 9.1174 13.6268 8.30445 13.135 7.64965L14.1985 6.85082C14.8574 7.72804 15.2483 8.81952 15.2483 10.0002L15.2336 10.3938C15.166 11.3044 14.8657 12.1515 14.3918 12.8743L14.3069 12.9797C14.0889 13.199 13.7396 13.2418 13.4709 13.0657C13.164 12.8643 13.0784 12.4528 13.2795 12.1457L13.4231 11.9084C13.6935 11.4246 13.8643 10.8776 13.9075 10.2942L13.9182 10.0002ZM13.2678 6.71801C13.5615 6.49772 13.978 6.55727 14.1985 6.85082L13.135 7.64965C12.9144 7.35599 12.9742 6.93858 13.2678 6.71801ZM16.5911 5.44555C16.9138 5.27033 17.3171 5.38949 17.4924 5.71215L16.3235 6.34691C16.1483 6.02419 16.2684 5.62081 16.5911 5.44555ZM11.0813 15.9084C11.0813 17.5226 9.22237 18.3912 7.9895 17.4202L7.87231 17.3205L5.65552 15.2873C5.33557 14.9941 4.91667 14.8313 4.48267 14.8313C2.7902 14.8311 1.41821 13.4594 1.41821 11.7668V8.23363C1.41821 6.54111 2.7902 5.16843 4.48267 5.1682L4.64478 5.16039C5.02003 5.12526 5.37552 4.96881 5.65552 4.71215L7.87231 2.67992L7.9895 2.58031C9.22237 1.60902 11.0813 2.47773 11.0813 4.09203V15.9084Z"></path></svg></span>
                              </button>
                              {/* <button><span><svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg" class="icon"><path d="M12.0303 4.11328C13.4406 2.70317 15.7275 2.70305 17.1377 4.11328C18.5474 5.52355 18.5476 7.81057 17.1377 9.2207L10.8457 15.5117C10.522 15.8354 10.2868 16.0723 10.0547 16.2627L9.82031 16.4395C9.61539 16.5794 9.39783 16.7003 9.1709 16.7998L8.94141 16.8916C8.75976 16.9582 8.57206 17.0072 8.35547 17.0518L7.59082 17.1865L5.19727 17.5859C5.05455 17.6097 4.90286 17.6358 4.77441 17.6455C4.67576 17.653 4.54196 17.6555 4.39648 17.6201L4.24707 17.5703C4.02415 17.4746 3.84119 17.3068 3.72559 17.0957L3.67969 17.0029C3.59322 16.8013 3.59553 16.6073 3.60547 16.4756C3.61519 16.3473 3.6403 16.1963 3.66406 16.0537L4.06348 13.6602C4.1638 13.0582 4.22517 12.6732 4.3584 12.3096L4.45117 12.0791C4.55073 11.8521 4.67152 11.6346 4.81152 11.4297L4.9873 11.1953C5.17772 10.9632 5.4146 10.728 5.73828 10.4043L12.0303 4.11328ZM6.67871 11.3447C6.32926 11.6942 6.14542 11.8803 6.01953 12.0332L5.90918 12.1797C5.81574 12.3165 5.73539 12.4618 5.66895 12.6133L5.60742 12.7666C5.52668 12.9869 5.48332 13.229 5.375 13.8789L4.97656 16.2725L4.97559 16.2744H4.97852L7.37207 15.875L8.08887 15.749C8.25765 15.7147 8.37336 15.6839 8.4834 15.6436L8.63672 15.5811C8.78817 15.5146 8.93356 15.4342 9.07031 15.3408L9.2168 15.2305C9.36965 15.1046 9.55583 14.9207 9.90527 14.5713L14.8926 9.58301L11.666 6.35742L6.67871 11.3447ZM16.1963 5.05371C15.3054 4.16304 13.8616 4.16305 12.9707 5.05371L12.6074 5.41602L15.833 8.64258L16.1963 8.2793C17.0869 7.38845 17.0869 5.94456 16.1963 5.05371Z"></path><path d="M4.58301 1.7832C4.72589 1.7832 4.84877 1.88437 4.87695 2.02441C4.99384 2.60873 5.22432 3.11642 5.58398 3.50391C5.94115 3.88854 6.44253 4.172 7.13281 4.28711C7.27713 4.3114 7.38267 4.43665 7.38281 4.58301C7.38281 4.7295 7.27723 4.8546 7.13281 4.87891C6.44249 4.99401 5.94116 5.27746 5.58398 5.66211C5.26908 6.00126 5.05404 6.43267 4.92676 6.92676L4.87695 7.1416C4.84891 7.28183 4.72601 7.38281 4.58301 7.38281C4.44013 7.38267 4.31709 7.28173 4.28906 7.1416C4.17212 6.55728 3.94179 6.04956 3.58203 5.66211C3.22483 5.27757 2.72347 4.99395 2.0332 4.87891C1.88897 4.85446 1.7832 4.72938 1.7832 4.58301C1.78335 4.43673 1.88902 4.3115 2.0332 4.28711C2.72366 4.17203 3.22481 3.88861 3.58203 3.50391C3.94186 3.11638 4.17214 2.60888 4.28906 2.02441L4.30371 1.97363C4.34801 1.86052 4.45804 1.78333 4.58301 1.7832Z"></path></svg></span></button>
                              <button><span><svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg" class="icon"><path d="M3.502 16.6663V13.3333C3.502 12.9661 3.79977 12.6683 4.16704 12.6683H7.50004L7.63383 12.682C7.93691 12.7439 8.16508 13.0119 8.16508 13.3333C8.16508 13.6547 7.93691 13.9227 7.63383 13.9847L7.50004 13.9984H5.47465C6.58682 15.2249 8.21842 16.0013 10 16.0013C13.06 16.0012 15.5859 13.711 15.9551 10.7513L15.9854 10.6195C16.0845 10.3266 16.3785 10.1334 16.6973 10.1732C17.0617 10.2186 17.3198 10.551 17.2745 10.9154L17.2247 11.2523C16.6301 14.7051 13.6224 17.3313 10 17.3314C8.01103 17.3314 6.17188 16.5383 4.83208 15.2474V16.6663C4.83208 17.0335 4.53411 17.3311 4.16704 17.3314C3.79977 17.3314 3.502 17.0336 3.502 16.6663ZM4.04497 9.24935C3.99936 9.61353 3.66701 9.87178 3.30278 9.8265C2.93833 9.78105 2.67921 9.44876 2.72465 9.08431L4.04497 9.24935ZM10 2.66829C11.9939 2.66833 13.8372 3.46551 15.1778 4.76204V3.33333C15.1778 2.96616 15.4757 2.66844 15.8428 2.66829C16.2101 2.66829 16.5079 2.96606 16.5079 3.33333V6.66634C16.5079 7.03361 16.2101 7.33138 15.8428 7.33138H12.5098C12.1425 7.33138 11.8448 7.03361 11.8448 6.66634C11.8449 6.29922 12.1426 6.0013 12.5098 6.0013H14.5254C13.4133 4.77488 11.7816 3.99841 10 3.99837C6.93998 3.99837 4.41406 6.28947 4.04497 9.24935L3.38481 9.16634L2.72465 9.08431C3.17574 5.46702 6.26076 2.66829 10 2.66829Z"></path></svg></span></button>
                              <button><span><svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg" class="icon"><path d="M2.66821 12.6663V12.5003C2.66821 12.1331 2.96598 11.8353 3.33325 11.8353C3.70052 11.8353 3.99829 12.1331 3.99829 12.5003V12.6663C3.99829 13.3772 3.9992 13.8707 4.03052 14.2542C4.0612 14.6298 4.11803 14.8413 4.19849 14.9993L4.2688 15.1263C4.44511 15.4137 4.69813 15.6481 5.00024 15.8021L5.13013 15.8577C5.2739 15.9092 5.46341 15.947 5.74536 15.97C6.12888 16.0014 6.62221 16.0013 7.33325 16.0013H12.6663C13.3771 16.0013 13.8707 16.0014 14.2542 15.97C14.6295 15.9394 14.8413 15.8825 14.9993 15.8021L15.1262 15.7308C15.4136 15.5545 15.6481 15.3014 15.802 14.9993L15.8577 14.8695C15.9091 14.7257 15.9469 14.536 15.97 14.2542C16.0013 13.8707 16.0012 13.3772 16.0012 12.6663V12.5003C16.0012 12.1332 16.2991 11.8355 16.6663 11.8353C17.0335 11.8353 17.3313 12.1331 17.3313 12.5003V12.6663C17.3313 13.3553 17.3319 13.9124 17.2952 14.3626C17.2624 14.7636 17.1974 15.1247 17.053 15.4613L16.9866 15.6038C16.7211 16.1248 16.3172 16.5605 15.8215 16.8646L15.6038 16.9866C15.227 17.1786 14.8206 17.2578 14.3625 17.2952C13.9123 17.332 13.3553 17.3314 12.6663 17.3314H7.33325C6.64416 17.3314 6.0872 17.332 5.63696 17.2952C5.23642 17.2625 4.87552 17.1982 4.53931 17.054L4.39673 16.9866C3.87561 16.7211 3.43911 16.3174 3.13501 15.8216L3.01294 15.6038C2.82097 15.2271 2.74177 14.8206 2.70435 14.3626C2.66758 13.9124 2.66821 13.3553 2.66821 12.6663ZM9.33521 12.5003V4.9388L7.13696 7.13704C6.87732 7.39668 6.45625 7.39657 6.19653 7.13704C5.93684 6.87734 5.93684 6.45631 6.19653 6.19661L9.52954 2.86263L9.6311 2.77962C9.73949 2.70742 9.86809 2.66829 10.0002 2.66829C10.1763 2.66838 10.3454 2.73819 10.47 2.86263L13.804 6.19661C14.0633 6.45628 14.0634 6.87744 13.804 7.13704C13.5443 7.39674 13.1222 7.39674 12.8625 7.13704L10.6653 4.93977V12.5003C10.6651 12.8673 10.3673 13.1652 10.0002 13.1654C9.63308 13.1654 9.33538 12.8674 9.33521 12.5003Z"></path></svg></span></button> */}
                            </div>
                          </div>
                        </div >
                      </>
                    ))}
                    {isLoading && (
                      <div className='message bot'>
                        <div className='typing-indicator'>
                          <span>Thinking</span>
                          <div class="snippet" data-title="dot-flashing">
                            <div class="stage">
                              <div class="dot-flashing"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {error && (
                      <div className='message error'>
                        Error: {error.message}
                        <button onClick={reload}>Retry</button>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                </div>
              }
              <div className='bottom-massage-box'>
                <form
                  onSubmit={handleFormSubmit}
                  className={`massage-box ${dragActive ? 'drag-active' : ''}`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  {/* Add this drag-drop overlay */}
                  {dragActive && (
                    <div className="drag-drop-overlay">
                      <div className="drag-drop-content">
                        <i className="icon">📁</i>
                        <p>Drop files here to upload</p>
                      </div>
                    </div>
                  )}

                  {/* Add this file preview section */}
                  {filePreviews.length > 0 && (
                    <div className="file-previews">
                      {filePreviews.map((file) => (
                        <div key={file.id} className="file-preview">
                          {file.type === 'image' ? (
                            <img src={file.url} alt={file.name} />
                          ) : (
                            <div className="file-icon">
                              <i className="icon">📄</i>
                              <span>{file.name}</span>
                            </div>
                          )}
                          <button
                            type="button"
                            className="remove-file"
                            onClick={() => removeFile(file.id)}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ position: 'relative', width: '100%' }}>
                    <TextField
                      fullWidth
                      inputRef={inputRef}
                      hiddenLabel
                      size="small"
                      multiline
                      minRows={1}
                      maxRows={6}
                      name='chat'
                      value={input}
                      variant="standard"
                      onChange={handleCustomInputChange}
                      onKeyDown={handleKeyDown}
                      // disabled={isLoading || mcpLoading}
                      placeholder='Type /tools or @toolname to get started'
                      sx={{
                        '& .MuiInputBase-input': {
                          padding: '8px 4px',
                          fontSize: '0.95rem',
                          lineHeight: '1.3'
                        },
                        '& .MuiInput-underline:before': {
                          borderBottom: 'none'
                        },
                        '& .MuiInput-underline:after': {
                          borderBottom: 'none'
                        },
                        '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                          borderBottom: 'none'
                        }
                      }}
                    />

                    {/* Tool Mention Dropdown */}
                    {showToolDropdown && filteredTools.length > 0 && (
                      <div
                        ref={toolDropdownRef}
                        className="tool-mention-dropdown"
                      >
                        {filteredTools.map((tool, index) => (
                          <div
                            key={tool.id}
                            onClick={() => insertToolMention(tool)}
                            className={`tool-dropdown-item ${index === selectedToolIndex ? 'selected' : ''}`}
                            onMouseEnter={() => setSelectedToolIndex(index)}
                          >
                            <span className="tool-icon">{tool.icon}</span>
                            <span className="tool-name">
                              {tool.name}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className='tootls-area'>
                    <div className='left-area'>
                      <div className='add-mmore-file' >
                        <button type="button" className='tools-btn plus-btn' onChange={handleFileChange}>
                          <i className='icon' >
                            <svg width="800px" height="800px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M19.463 5.576c-.688-.75-1.929-.796-2.756.031l-8.1 8.1c-.21.21-.21.476 0 .686.21.21.476.21.686 0l6.7-6.7a1 1 0 0 1 1.414 1.414l-6.7 6.7a2.45 2.45 0 0 1-3.514 0 2.45 2.45 0 0 1 0-3.514l8.1-8.1c1.567-1.568 4.115-1.619 5.63.015 1.552 1.569 1.597 4.104-.03 5.613l-9.486 9.486c-2.19 2.19-5.624 2.19-7.814 0-2.19-2.19-2.19-5.624 0-7.814l8.1-8.1a1 1 0 0 1 1.414 1.414l-8.1 8.1c-1.41 1.41-1.41 3.576 0 4.986 1.41 1.41 3.576 1.41 4.986 0l9.5-9.5.031-.03c.75-.687.796-1.929-.031-2.756l-.03-.031z" fill="currentColor" /></svg>
                            <input type='file' name='files' />
                          </i>
                          {/* <input 
                              type='file' 
                              name='files' 
                              multiple
                              onChange={handleFileChange}
                              accept="image/*,.pdf,.doc,.docx,.txt"
                            /> */}
                        </button>
                        {/* <button type="submit" className='send-btn'></button> */}
                        {/* {dropdownOpenFiles && (
                          <div className='add-files-dropdown'>
                            <div className='dropdown-item-box'>
                              <i className='icon'>
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg" class="icon" aria-hidden="true"><path d="M14.3352 17.5003V15.6654H12.5002C12.1331 15.6654 11.8354 15.3674 11.8352 15.0003C11.8352 14.6331 12.133 14.3353 12.5002 14.3353H14.3352V12.5003C14.3352 12.1331 14.633 11.8353 15.0002 11.8353C15.3674 11.8355 15.6653 12.1332 15.6653 12.5003V14.3353H17.5002L17.634 14.349C17.937 14.411 18.1653 14.679 18.1653 15.0003C18.1651 15.3215 17.9369 15.5897 17.634 15.6517L17.5002 15.6654H15.6653V17.5003C15.6651 17.8673 15.3673 18.1652 15.0002 18.1654C14.6331 18.1654 14.3354 17.8674 14.3352 17.5003ZM16.0012 8.33333V7.33333C16.0012 6.62229 16.0013 6.12896 15.97 5.74544C15.9469 5.46349 15.9091 5.27398 15.8577 5.1302L15.802 5.00032C15.6481 4.69821 15.4137 4.44519 15.1262 4.26888L14.9993 4.19856C14.8413 4.11811 14.6297 4.06128 14.2542 4.03059C13.8707 3.99928 13.3772 3.99837 12.6663 3.99837H9.16431C9.16438 4.04639 9.15951 4.09505 9.14868 4.14388L8.61646 4.02571H8.61548L9.14868 4.14388L8.69263 6.19954C8.5874 6.67309 8.50752 7.06283 8.33911 7.3929L8.26196 7.53059C8.12314 7.75262 7.94729 7.94837 7.74341 8.11067L7.53052 8.26204C7.26187 8.42999 6.95158 8.52024 6.58521 8.60579L6.19946 8.6927L4.1438 9.14876L4.02564 8.61556V8.61653L4.1438 9.14876C4.09497 9.15959 4.04631 9.16446 3.99829 9.16438V12.6663C3.99829 13.3772 3.9992 13.8707 4.03052 14.2542C4.0612 14.6298 4.11803 14.8413 4.19849 14.9993L4.2688 15.1263C4.44511 15.4137 4.69813 15.6481 5.00024 15.8021L5.13013 15.8577C5.2739 15.9092 5.46341 15.947 5.74536 15.97C6.12888 16.0014 6.62221 16.0013 7.33325 16.0013H12.6663C13.3771 16.0013 13.8707 16.0014 14.2542 15.97C14.6295 15.9394 14.8413 15.8825 14.9993 15.8021L15.1262 15.7308C15.4136 15.5545 15.6481 15.3014 15.802 14.9993L15.8577 14.8695C15.9091 14.7257 15.9469 14.536 15.97 14.2542C16.0013 13.8707 16.0012 13.3772 16.0012 12.6663V12.5003C16.0012 12.1332 16.2991 11.8355 16.6663 11.8353C17.0335 11.8353 17.3313 12.1331 17.3313 12.5003V12.6663C17.3313 13.3553 17.3319 13.9124 17.2952 14.3626C17.2624 14.7636 17.1974 15.1247 17.053 15.4613L16.9866 15.6038C16.7211 16.1248 16.3172 16.5605 15.8215 16.8646L15.6038 16.9866C15.227 17.1786 14.8206 17.2578 14.3625 17.2952C13.9123 17.332 13.3553 17.3314 12.6663 17.3314H7.33325C6.64416 17.3314 6.0872 17.332 5.63696 17.2952C5.23642 17.2625 4.87552 17.1982 4.53931 17.054L4.39673 16.9866C3.87561 16.7211 3.43911 16.3174 3.13501 15.8216L3.01294 15.6038C2.82097 15.2271 2.74177 14.8206 2.70435 14.3626C2.66758 13.9124 2.66821 13.3553 2.66821 12.6663V9.00032C2.66821 7.44077 3.58925 5.86261 4.76196 4.70638C5.9331 3.55172 7.50845 2.6675 9.00024 2.66927V2.66829H12.6663C13.3553 2.66829 13.9123 2.66765 14.3625 2.70442C14.8206 2.74184 15.227 2.82105 15.6038 3.01302L15.8215 3.13509C16.3174 3.43919 16.7211 3.87569 16.9866 4.39681L17.053 4.53938C17.1973 4.8757 17.2624 5.23636 17.2952 5.63704C17.332 6.08728 17.3313 6.64424 17.3313 7.33333V8.33333C17.3313 8.7006 17.0335 8.99837 16.6663 8.99837C16.2991 8.99819 16.0012 8.70049 16.0012 8.33333ZM7.76001 4.26204C7.06176 4.53903 6.3362 5.02201 5.69556 5.65364C5.04212 6.29794 4.53764 7.03653 4.25415 7.76204L5.91138 7.39388L6.30689 7.30403C6.62563 7.22833 6.73681 7.18952 6.82544 7.13411L6.91528 7.07063C7.00136 7.00214 7.07542 6.91924 7.13403 6.82552L7.18579 6.722C7.23608 6.59782 7.28758 6.38943 7.3938 5.91145L7.76001 4.26204Z"></path></svg>
                              </i>
                              Add photos and files
                              <input type='file' name='files' />
                            </div>
                            <div className='dropdown-item-box'>
                              <i className='icon'>
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg" class="icon" aria-hidden="true"><path d="M7.94556 14.0277C7.9455 12.9376 7.06204 12.054 5.97192 12.054C4.88191 12.0542 3.99835 12.9376 3.99829 14.0277C3.99829 15.1177 4.88188 16.0012 5.97192 16.0013C7.06207 16.0013 7.94556 15.1178 7.94556 14.0277ZM13.3625 16.6663V14.6927H11.3889C11.0216 14.6927 10.7239 14.3949 10.7239 14.0277C10.7239 13.6605 11.0217 13.3626 11.3889 13.3626H13.3625V11.389C13.3625 11.0218 13.6604 10.724 14.0276 10.724C14.3949 10.724 14.6926 11.0217 14.6926 11.389V13.3626H16.6663L16.801 13.3763C17.1038 13.4385 17.3312 13.7065 17.3313 14.0277C17.3313 14.3489 17.1038 14.6169 16.801 14.679L16.6663 14.6927H14.6926V16.6663C14.6926 17.0336 14.3949 17.3314 14.0276 17.3314C13.6604 17.3313 13.3625 17.0336 13.3625 16.6663ZM7.94556 5.97201C7.94544 4.88196 7.062 3.99837 5.97192 3.99837C4.88195 3.99849 3.99841 4.88203 3.99829 5.97201C3.99829 7.06208 4.88187 7.94552 5.97192 7.94564C7.06207 7.94564 7.94556 7.06216 7.94556 5.97201ZM16.0012 5.97201C16.0011 4.88196 15.1177 3.99837 14.0276 3.99837C12.9376 3.99843 12.0541 4.882 12.054 5.97201C12.054 7.06212 12.9375 7.94558 14.0276 7.94564C15.1177 7.94564 16.0012 7.06216 16.0012 5.97201ZM9.27563 14.0277C9.27563 15.8524 7.79661 17.3314 5.97192 17.3314C4.14734 17.3313 2.66821 15.8523 2.66821 14.0277C2.66827 12.2031 4.14737 10.7241 5.97192 10.724C7.79657 10.724 9.27558 12.203 9.27563 14.0277ZM9.27563 5.97201C9.27563 7.7967 7.79661 9.27572 5.97192 9.27572C4.14734 9.2756 2.66821 7.79662 2.66821 5.97201C2.66833 4.14749 4.14741 2.66841 5.97192 2.6683C7.79654 2.6683 9.27552 4.14742 9.27563 5.97201ZM17.3313 5.97201C17.3313 7.79669 15.8523 9.27572 14.0276 9.27572C12.203 9.27566 10.7239 7.79666 10.7239 5.97201C10.724 4.14746 12.203 2.66836 14.0276 2.6683C15.8522 2.6683 17.3312 4.14742 17.3313 5.97201Z"></path></svg>
                              </i>
                              Add from apps
                              <i className='icon-right fa fa-angle-right'></i>
                              <div className='sub-menu add-files-dropdown'>
                                <div className='dropdown-item-box'>
                                  <i className='icon'>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon" aria-hidden="true"><path d="M3.51105 18.4269L4.39307 19.9615C4.57634 20.2846 4.8398 20.5384 5.14908 20.723L8.29914 15.2307H1.99902C1.99902 15.5884 2.09066 15.9461 2.27394 16.2692L3.51105 18.4269Z" fill="#0066DA"></path><path d="M11.999 8.76916L8.84897 3.27686C8.53969 3.46147 8.27623 3.71532 8.09295 4.03839L2.27394 14.1922C2.09403 14.5083 1.99926 14.8663 1.99902 15.2307H8.29914L11.999 8.76916Z" fill="#00AC47"></path><path d="M18.8492 20.723C19.1585 20.5384 19.422 20.2846 19.6053 19.9615L19.9718 19.3269L21.7244 16.2692C21.9077 15.9461 21.9993 15.5884 21.9993 15.2307H15.6987L17.0394 17.8846L18.8492 20.723Z" fill="#EA4335"></path><path d="M11.9982 8.76923L15.1483 3.27692C14.839 3.09231 14.4839 3 14.1173 3H9.87907C9.51252 3 9.15742 3.10385 8.84814 3.27692L11.9982 8.76923Z" fill="#00832D"></path><path d="M15.6983 15.2307H8.29849L5.14844 20.723C5.45772 20.9076 5.81281 20.9999 6.17937 20.9999H17.8174C18.1839 20.9999 18.539 20.8961 18.8483 20.723L15.6983 15.2307Z" fill="#2684FC"></path><path d="M18.8146 9.11532L15.9051 4.03839C15.7218 3.71532 15.4584 3.46147 15.1491 3.27686L11.999 8.76916L15.6989 15.2307H21.9876C21.9876 14.873 21.8959 14.5153 21.7127 14.1922L18.8146 9.11532Z" fill="#FFBA00"></path></svg>
                                  </i>
                                  Connect Google Drive
                                </div>
                                <div className='dropdown-item-box'>
                                  <i className='icon'>
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon" aria-hidden="true"><path d="M7.82373 7.35312L7.82391 7.35249L11.6726 9.65779L13.9659 8.6927C14.4319 8.49124 14.9344 8.38793 15.4421 8.38843C15.5267 8.38843 15.6103 8.39227 15.6935 8.39782C15.4178 7.32269 14.8357 6.35079 14.0181 5.60023C13.2004 4.84966 12.1823 4.35275 11.0876 4.16987C9.99283 3.98699 8.86853 4.12601 7.8513 4.57005C6.83408 5.01409 5.96772 5.74403 5.35751 6.67115C5.37712 6.67091 5.39643 6.66968 5.41608 6.66968C6.26666 6.66852 7.10061 6.90525 7.82373 7.35312Z" fill="#0364B8"></path><path d="M7.82449 7.3525L7.82431 7.35313C7.10119 6.90526 6.26724 6.66853 5.41666 6.66968C5.39701 6.66968 5.37767 6.67091 5.35809 6.67116C4.52559 6.68147 3.71163 6.91843 3.00368 7.35659C2.29574 7.79475 1.72058 8.41754 1.34 9.15804C0.959422 9.89853 0.787821 10.7287 0.843637 11.5594C0.899452 12.3901 1.18057 13.1899 1.65679 13.8728L5.05075 12.4446L6.55948 11.8097L9.91879 10.396L11.6731 9.65779L7.82449 7.3525Z" fill="#0078D4"></path><path d="M15.6938 8.39782C15.6106 8.39227 15.527 8.38843 15.4424 8.38843C14.9347 8.38793 14.4323 8.49163 13.9663 8.6931L11.6728 9.65779L12.3378 10.0561L14.5178 11.3619L15.4688 11.9316L18.7209 13.8795C19.0164 13.331 19.1695 12.717 19.1663 12.0939C19.163 11.4709 19.0035 10.8586 18.7023 10.3131C18.4011 9.76768 17.9678 9.30653 17.4422 8.97191C16.9166 8.63729 16.3154 8.4399 15.6938 8.39782Z" fill="#1490DF"></path><path d="M15.4693 11.9315L14.5182 11.3618L12.3383 10.0561L11.6732 9.65771L9.9189 10.396L6.55959 11.8096L5.05086 12.4445L1.6569 13.8727C2.07866 14.4791 2.64091 14.9744 3.29564 15.3163C3.95037 15.6583 4.67813 15.8367 5.41676 15.8363H15.4428C16.115 15.8365 16.7748 15.6547 17.352 15.3101C17.9292 14.9656 18.4024 14.4712 18.7213 13.8794L15.4693 11.9315Z" fill="#28A8EA"></path></svg>
                                  </i>
                                  Connect Microsoft OneDrive
                                  <span className='sub-text'>Persnol</span>
                                </div>
                                <div className='dropdown-item-box'>
                                  <i className='icon'>
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon" aria-hidden="true"><path d="M7.82373 7.35312L7.82391 7.35249L11.6726 9.65779L13.9659 8.6927C14.4319 8.49124 14.9344 8.38793 15.4421 8.38843C15.5267 8.38843 15.6103 8.39227 15.6935 8.39782C15.4178 7.32269 14.8357 6.35079 14.0181 5.60023C13.2004 4.84966 12.1823 4.35275 11.0876 4.16987C9.99283 3.98699 8.86853 4.12601 7.8513 4.57005C6.83408 5.01409 5.96772 5.74403 5.35751 6.67115C5.37712 6.67091 5.39643 6.66968 5.41608 6.66968C6.26666 6.66852 7.10061 6.90525 7.82373 7.35312Z" fill="#0364B8"></path><path d="M7.82449 7.3525L7.82431 7.35313C7.10119 6.90526 6.26724 6.66853 5.41666 6.66968C5.39701 6.66968 5.37767 6.67091 5.35809 6.67116C4.52559 6.68147 3.71163 6.91843 3.00368 7.35659C2.29574 7.79475 1.72058 8.41754 1.34 9.15804C0.959422 9.89853 0.787821 10.7287 0.843637 11.5594C0.899452 12.3901 1.18057 13.1899 1.65679 13.8728L5.05075 12.4446L6.55948 11.8097L9.91879 10.396L11.6731 9.65779L7.82449 7.3525Z" fill="#0078D4"></path><path d="M15.6938 8.39782C15.6106 8.39227 15.527 8.38843 15.4424 8.38843C14.9347 8.38793 14.4323 8.49163 13.9663 8.6931L11.6728 9.65779L12.3378 10.0561L14.5178 11.3619L15.4688 11.9316L18.7209 13.8795C19.0164 13.331 19.1695 12.717 19.1663 12.0939C19.163 11.4709 19.0035 10.8586 18.7023 10.3131C18.4011 9.76768 17.9678 9.30653 17.4422 8.97191C16.9166 8.63729 16.3154 8.4399 15.6938 8.39782Z" fill="#1490DF"></path><path d="M15.4693 11.9315L14.5182 11.3618L12.3383 10.0561L11.6732 9.65771L9.9189 10.396L6.55959 11.8096L5.05086 12.4445L1.6569 13.8727C2.07866 14.4791 2.64091 14.9744 3.29564 15.3163C3.95037 15.6583 4.67813 15.8367 5.41676 15.8363H15.4428C16.115 15.8365 16.7748 15.6547 17.352 15.3101C17.9292 14.9656 18.4024 14.4712 18.7213 13.8794L15.4693 11.9315Z" fill="#28A8EA"></path></svg>
                                  </i>
                                  Connect Microsoft OneDrive
                                  <span className='sub-text'>Work/School - Includes SharePoint</span>
                                </div>
                              </div>

                            </div>
                          </div>
                        )} */}
                      </div>

                      <div className='add-mmore-file' >
                        <button type="button" className='tools-btn' onClick={() => {
                          if (!dropdownOpenTools) dispatch(getAllMCPServers())
                          setDropdownOpenTools(!dropdownOpenTools)
                        }}>
                          <i className='icon'>
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-label="" class="icon"><path d="M7.91626 11.0013C9.43597 11.0013 10.7053 12.0729 11.011 13.5013H16.6663L16.801 13.515C17.1038 13.5771 17.3311 13.8453 17.3313 14.1663C17.3313 14.4875 17.1038 14.7555 16.801 14.8177L16.6663 14.8314H11.011C10.7056 16.2601 9.43619 17.3314 7.91626 17.3314C6.39643 17.3312 5.1269 16.2601 4.82153 14.8314H3.33325C2.96598 14.8314 2.66821 14.5336 2.66821 14.1663C2.66839 13.7992 2.96609 13.5013 3.33325 13.5013H4.82153C5.12713 12.0729 6.39665 11.0015 7.91626 11.0013ZM7.91626 12.3314C6.90308 12.3316 6.08148 13.1532 6.0813 14.1663C6.0813 15.1797 6.90297 16.0011 7.91626 16.0013C8.9297 16.0013 9.75122 15.1798 9.75122 14.1663C9.75104 13.153 8.92959 12.3314 7.91626 12.3314ZM12.0833 2.66829C13.6031 2.66829 14.8725 3.73966 15.178 5.16829H16.6663L16.801 5.18196C17.1038 5.24414 17.3313 5.51212 17.3313 5.83333C17.3313 6.15454 17.1038 6.42253 16.801 6.4847L16.6663 6.49837H15.178C14.8725 7.92701 13.6031 8.99837 12.0833 8.99837C10.5634 8.99837 9.29405 7.92701 8.98853 6.49837H3.33325C2.96598 6.49837 2.66821 6.2006 2.66821 5.83333C2.66821 5.46606 2.96598 5.16829 3.33325 5.16829H8.98853C9.29405 3.73966 10.5634 2.66829 12.0833 2.66829ZM12.0833 3.99837C11.0698 3.99837 10.2483 4.81989 10.2483 5.83333C10.2483 6.84677 11.0698 7.66829 12.0833 7.66829C13.0967 7.66829 13.9182 6.84677 13.9182 5.83333C13.9182 4.81989 13.0967 3.99837 12.0833 3.99837Z"></path></svg>
                          </i>
                          Tools
                        </button>
                        {dropdownOpenTools && (
                          <div className='add-files-dropdown style-two'  ref={dropdownRef}>
                            {/* MCP Server Tools Submenu */}
                            {mcpServer['tools'] && mcpServer['tools'].length > 0
                              ? <div className='add-files-dropdown add-sub-dropdown'>
                                <div className='searchbox-dropdown'>
                                  <button className='back-btn' onClick={() => { setMcpServer({}) }}><i className='icon fa fa-angle-left'></i></button>
                                  <TextField
                                    fullWidth
                                    hiddenLabel
                                    size='small'
                                    name='search'
                                    variant='standard'
                                    value={toolFilter}
                                    placeholder='Search...'
                                    onChange={(e) => setToolFilter(e['target']['value'])}
                                  />
                                </div>
                                {/* Only show Disable All Tools for non-default servers */}
                                {mcpServer['status'] !== 'default' && (
                                  <div className='dropdown-item-box' onClick={(e) => { e.stopPropagation(); dispatch(disconnectMCPServer(mcpServer['name'])); setMcpServer({}) }}>
                                    <div className='left-area' onClick={(e) => { e.stopPropagation(); }}> {mcpServer['icon'] ? <i className='icon'> <img src={mcpServer['icon']} alt="" /> </i> : <i className='icon-word'> {mcpServer['name'].slice(0, 1)} </i>} Disable All Tools</div>
                                    <Switch onClick={(e) => { e.stopPropagation(); }} checked={mcpServer['connected']} />
                                  </div>
                                )}
                                {mcpServer['tools'].filter((tool) => tool['name'].toLowerCase().includes(toolFilter.toLowerCase())).map((tool) =>
                                  <div className='dropdown-item-box'>
                                    <div className='left-area'>{mcpServer['icon'] ? <i className='icon'> <img src={mcpServer['icon']} alt="" /> </i> : <i className='icon-word'> {mcpServer['name'].slice(0, 1)} </i>} {tool['name']}</div>
                                  </div>
                                )}
                              </div>
                              /* SellAgent Provider Tools Submenu */
                              : mcpServer['provider'] ? <div className='add-files-dropdown add-sub-dropdown'>
                                <div className='searchbox-dropdown'>
                                  <button className='back-btn' onClick={() => { setMcpServer({}); setToolFilter(''); }}><i className='icon fa fa-angle-left'></i></button>
                                  <TextField
                                    fullWidth
                                    hiddenLabel
                                    size='small'
                                    name='search'
                                    variant='standard'
                                    value={toolFilter}
                                    placeholder='Search tools...'
                                    onChange={(e) => setToolFilter(e['target']['value'])}
                                  />
                                </div>
                                {mcpServer['requiresAuth'] && !mcpServer['isConnected'] && (
                                  <div className='dropdown-item-box connect-prompt' onClick={(e) => { e.stopPropagation(); dispatch(createIntegration({ name: mcpServer['integrationName'], authType: 'oAuth2' })); }}>
                                    <i className='icon'>🔗</i> Connect {mcpServer['displayName']} to use these tools
                                  </div>
                                )}
                                {mcpServer['tools']?.filter(tool => tool['toolDisplayName']?.toLowerCase().includes(toolFilter.toLowerCase()) || tool['name']?.toLowerCase().includes(toolFilter.toLowerCase())).map((tool, idx) => (
                                  <div key={idx} className={`dropdown-item-box ${mcpServer['requiresAuth'] && !mcpServer['isConnected'] ? 'disabled' : ''}`} onClick={(e) => { e.stopPropagation(); if (mcpServer['requiresAuth'] && !mcpServer['isConnected']) dispatch(createIntegration({ name: mcpServer['integrationName'], authType: 'oAuth2' })); }} title={mcpServer['requiresAuth'] && !mcpServer['isConnected'] ? `Connect ${mcpServer['displayName']} to use this tool` : tool['description']}>
                                    <div className='left-area'>
                                      {mcpServer['icon'] ? <i className='icon'><img src={mcpServer['icon']} alt="" /></i> : <i className='icon-word'>{tool['toolDisplayName']?.slice(0, 1)}</i>}
                                      {tool['toolDisplayName']}
                                    </div>
                                    {mcpServer['requiresAuth'] && !mcpServer['isConnected'] && <span className='lock-icon'>🔒</span>}
                                  </div>
                                ))}
                              </div>
                              :
                              <div className='add-files-dropdown style-two' onClick={(e) => { e.stopPropagation(); }}>
                                <div className='dropdown-item-box' onClick={(e) => { e.stopPropagation(); }}>
                                  <i className='icon'> <img src={webSearchIcon} alt="" /> </i> Web Search <i className='icon-right'><Switch /></i>
                                </div>

                                {/* SellAgent MCP Server - Provider Groups */}
                                {(() => {
                                  const sellAgentServer = mcpServers?.find(s => s['name']?.toLowerCase() === 'sellagent');
                                  if (!sellAgentServer?.['providers']) return null;

                                  const getProviderIcon = (provider) => { try { const icon = require(`../../static/img/${provider}.png`); return icon.default || icon; } catch (e) { return null; } };
                                  const providerOrder = ['gmail', 'calendar', 'sellagent'];
                                  const sortedProviders = [...providerOrder.filter(p => sellAgentServer['providers'][p]), ...Object.keys(sellAgentServer['providers']).filter(p => !providerOrder.includes(p))];

                                  return sortedProviders.map(providerKey => {
                                    const provider = sellAgentServer['providers'][providerKey];
                                    const providerIcon = getProviderIcon(providerKey);

                                    return (
                                      <div key={providerKey} className={`dropdown-item-box provider-group ${provider['requiresAuth'] && !provider['isConnected'] ? 'needs-auth' : ''}`} onClick={(e) => { e.stopPropagation(); if (provider['requiresAuth'] && !provider['isConnected']) { dispatch(createIntegration({ name: provider['integrationName'], authType: 'oAuth2' })); } else { setMcpServer({ ...provider, provider: providerKey, icon: providerIcon }); setToolFilter(''); } }}>
                                        {providerIcon ? <i className='icon'><img src={providerIcon} alt="" /></i> : <i className='icon-word'>{provider['displayName']?.slice(0, 1)}</i>}
                                        {provider['displayName']}
                                        <i className='icon-right'>
                                          {provider['requiresAuth'] && !provider['isConnected'] ? (
                                            <p className='status-text connect-btn'>Connect</p>
                                          ) : (
                                            <>
                                              {provider['requiresAuth'] && provider['accountInfo'] && <p className='connected-email'>{provider['accountInfo']}</p>}
                                              <span className='tool-count'>{provider['tools']?.length || 0}</span>
                                              <i className='fa fa-angle-right'></i>
                                            </>
                                          )}
                                        </i>
                                      </div>
                                    );
                                  });
                                })()}

                                {/* Other MCP Servers (non-SellAgent) */}
                                {mcpServers && mcpServers.length > 0 && mcpServers.filter(server => server['name']?.toLowerCase() !== 'sellagent').map(server =>
                                  <React.Fragment key={server['name']}>
                                    {!server['connected'] &&
                                      <div className='dropdown-item-box' onClick={(e) => { e.stopPropagation(); dispatch(connectMCPServer(server['name'])); }}>
                                        {server['icon'] ? <i className='icon'><img src={server['icon']} alt="" /></i> : <i className='icon-word'>{server['name'].slice(0, 1)}</i>}
                                        {server['name']}
                                        <i className='icon-right'><Switch checked={server['connected']} /></i>
                                      </div>
                                    }
                                    {server['connected'] && server['tools'] && server['tools'].length > 0 &&
                                      <div className='dropdown-item-box' onClick={(e) => { e.stopPropagation(); setMcpServer(server); }}>
                                        {server['icon'] ? <i className='icon'><img src={server['icon']} alt="" /></i> : <i className='icon-word'>{server['name'].slice(0, 1)}</i>}
                                        {server['name']}
                                        <i className='icon-right fa fa-angle-right'></i>
                                      </div>
                                    }
                                  </React.Fragment>
                                )}
                                <div className='dropdown-item-box' onClick={() => setMcpManagerOpen(true)}>
                                  <i className='icon'>
                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M6 7.5C5.175 7.5 4.5 6.825 4.5 6C4.5 5.175 5.175 4.5 6 4.5C6.825 4.5 7.5 5.175 7.5 6C7.5 6.825 6.825 7.5 6 7.5ZM6 5.25C5.59 5.25 5.25 5.59 5.25 6C5.25 6.41 5.59 6.75 6 6.75C6.41 6.75 6.75 6.41 6.75 6C6.75 5.59 6.41 5.25 6 5.25Z" fill="var(--primary-color)" />
                                      <path d="M7.605 11.22C7.5 11.22 7.395 11.205 7.29 11.175C6.98 11.085 6.72 10.875 6.555 10.59L6.495 10.485C6.2 9.975 5.795 9.975 5.5 10.485L5.445 10.59C5.28 10.875 5.02 11.09 4.71 11.175C4.395 11.265 4.07 11.22 3.795 11.055L2.935 10.545C2.63 10.365 2.41 10.065 2.315 9.705C2.225 9.345 2.27 8.985 2.445 8.685C2.59 8.43 2.63 8.205 2.545 8.085C2.46 7.965 2.245 7.89 1.95 7.89C1.22 7.89 0.625 7.295 0.625 6.565V5.44C0.625 4.71 1.22 4.115 1.95 4.115C2.245 4.115 2.46 4.04 2.545 3.92C2.63 3.8 2.595 3.575 2.445 3.315C2.27 3.015 2.225 2.655 2.315 2.295C2.405 1.935 2.625 1.635 2.935 1.455L3.8 0.945C4.365 0.615 5.11 0.795 5.45 1.365L5.51 1.47C5.805 1.98 6.21 1.98 6.505 1.47L6.56 1.365C6.9 0.795 7.645 0.615 8.215 0.95L9.075 1.46C9.38 1.64 9.6 1.94 9.695 2.3C9.785 2.66 9.74 3.02 9.565 3.32C9.42 3.575 9.38 3.8 9.465 3.92C9.55 4.04 9.765 4.115 10.06 4.115C10.79 4.115 11.385 4.71 11.385 5.44V6.565C11.385 7.295 10.79 7.89 10.06 7.89C9.765 7.89 9.55 7.965 9.465 8.085C9.38 8.205 9.415 8.43 9.565 8.685C9.74 8.985 9.79 9.345 9.695 9.705C9.605 10.065 9.385 10.365 9.075 10.545L8.21 11.055C8.02 11.17 7.815 11.22 7.605 11.22ZM6 9.245C6.445 9.245 6.86 9.525 7.145 10.02L7.2 10.12C7.26 10.23 7.36 10.31 7.48 10.345C7.6 10.38 7.72 10.365 7.82 10.305L8.685 9.795C8.815 9.72 8.915 9.595 8.955 9.44C8.995 9.285 8.975 9.13 8.9 8.995C8.615 8.505 8.58 7.995 8.8 7.615C9.02 7.235 9.475 7.015 10.055 7.015C10.365 7.015 10.62 6.76 10.62 6.45V5.44C10.62 5.13 10.365 4.875 10.055 4.875C9.475 4.875 9.02 4.655 8.8 4.275C8.58 3.895 8.615 3.385 8.9 2.895C8.975 2.76 8.995 2.605 8.955 2.45C8.915 2.295 8.82 2.17 8.69 2.095L7.825 1.585C7.61 1.465 7.325 1.545 7.195 1.765L7.14 1.87C6.855 2.365 6.44 2.645 5.995 2.645C5.55 2.645 5.135 2.365 4.85 1.87L4.795 1.765C4.67 1.55 4.385 1.47 4.175 1.585L3.31 2.095C3.18 2.17 3.08 2.295 3.04 2.45C3 2.605 3.02 2.76 3.095 2.895C3.38 3.385 3.415 3.895 3.195 4.275C2.975 4.655 2.52 4.875 1.94 4.875C1.63 4.875 1.375 5.13 1.375 5.44V6.45C1.375 6.76 1.63 7.015 1.94 7.015C2.52 7.015 2.975 7.235 3.195 7.615C3.415 7.995 3.38 8.505 3.095 8.995C3.02 9.13 3 9.285 3.04 9.44C3.08 9.595 3.175 9.72 3.305 9.795L4.17 10.305C4.275 10.37 4.395 10.385 4.515 10.35C4.635 10.315 4.735 10.23 4.8 10.12L4.855 10.02C5.14 9.53 5.555 9.245 6 9.245Z" fill="var(--primary-color)" />
                                    </svg>
                                  </i>
                                  Integrate Tools
                                  <i className='icon-right'>
                                    <i>
                                      <svg width="800px" height="800px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <g id="SVGRepo_bgCarrier" stroke-width="0" />
                                        <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" />
                                        <g id="SVGRepo_iconCarrier"> <g id="Interface / External_Link"> <path id="Vector" d="M10.0002 5H8.2002C7.08009 5 6.51962 5 6.0918 5.21799C5.71547 5.40973 5.40973 5.71547 5.21799 6.0918C5 6.51962 5 7.08009 5 8.2002V15.8002C5 16.9203 5 17.4801 5.21799 17.9079C5.40973 18.2842 5.71547 18.5905 6.0918 18.7822C6.5192 19 7.07899 19 8.19691 19H15.8031C16.921 19 17.48 19 17.9074 18.7822C18.2837 18.5905 18.5905 18.2839 18.7822 17.9076C19 17.4802 19 16.921 19 15.8031V14M20 9V4M20 4H15M20 4L13 11" stroke="#4b5563" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /> </g> </g>
                                      </svg>
                                    </i>
                                  </i>
                                </div>
                                {/* <div className='dropdown-item-box'>
                                  <i className='icon'>
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg" class="icon" aria-hidden="true"><path d="M7.94556 14.0277C7.9455 12.9376 7.06204 12.054 5.97192 12.054C4.88191 12.0542 3.99835 12.9376 3.99829 14.0277C3.99829 15.1177 4.88188 16.0012 5.97192 16.0013C7.06207 16.0013 7.94556 15.1178 7.94556 14.0277ZM13.3625 16.6663V14.6927H11.3889C11.0216 14.6927 10.7239 14.3949 10.7239 14.0277C10.7239 13.6605 11.0217 13.3626 11.3889 13.3626H13.3625V11.389C13.3625 11.0218 13.6604 10.724 14.0276 10.724C14.3949 10.724 14.6926 11.0217 14.6926 11.389V13.3626H16.6663L16.801 13.3763C17.1038 13.4385 17.3312 13.7065 17.3313 14.0277C17.3313 14.3489 17.1038 14.6169 16.801 14.679L16.6663 14.6927H14.6926V16.6663C14.6926 17.0336 14.3949 17.3314 14.0276 17.3314C13.6604 17.3313 13.3625 17.0336 13.3625 16.6663ZM7.94556 5.97201C7.94544 4.88196 7.062 3.99837 5.97192 3.99837C4.88195 3.99849 3.99841 4.88203 3.99829 5.97201C3.99829 7.06208 4.88187 7.94552 5.97192 7.94564C7.06207 7.94564 7.94556 7.06216 7.94556 5.97201ZM16.0012 5.97201C16.0011 4.88196 15.1177 3.99837 14.0276 3.99837C12.9376 3.99843 12.0541 4.882 12.054 5.97201C12.054 7.06212 12.9375 7.94558 14.0276 7.94564C15.1177 7.94564 16.0012 7.06216 16.0012 5.97201ZM9.27563 14.0277C9.27563 15.8524 7.79661 17.3314 5.97192 17.3314C4.14734 17.3313 2.66821 15.8523 2.66821 14.0277C2.66827 12.2031 4.14737 10.7241 5.97192 10.724C7.79657 10.724 9.27558 12.203 9.27563 14.0277ZM9.27563 5.97201C9.27563 7.7967 7.79661 9.27572 5.97192 9.27572C4.14734 9.2756 2.66821 7.79662 2.66821 5.97201C2.66833 4.14749 4.14741 2.66841 5.97192 2.6683C7.79654 2.6683 9.27552 4.14742 9.27563 5.97201ZM17.3313 5.97201C17.3313 7.79669 15.8523 9.27572 14.0276 9.27572C12.203 9.27566 10.7239 7.79666 10.7239 5.97201C10.724 4.14746 12.203 2.66836 14.0276 2.6683C15.8522 2.6683 17.3312 4.14742 17.3313 5.97201Z"></path></svg>
                                  </i>
                                  Add from apps
                                  <i className='icon-right fa fa-angle-right'></i>
                                  <div className='sub-menu add-files-dropdown'>
                                    <div className='dropdown-item-box'>
                                      <i className='icon'>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon" aria-hidden="true"><path d="M3.51105 18.4269L4.39307 19.9615C4.57634 20.2846 4.8398 20.5384 5.14908 20.723L8.29914 15.2307H1.99902C1.99902 15.5884 2.09066 15.9461 2.27394 16.2692L3.51105 18.4269Z" fill="#0066DA"></path><path d="M11.999 8.76916L8.84897 3.27686C8.53969 3.46147 8.27623 3.71532 8.09295 4.03839L2.27394 14.1922C2.09403 14.5083 1.99926 14.8663 1.99902 15.2307H8.29914L11.999 8.76916Z" fill="#00AC47"></path><path d="M18.8492 20.723C19.1585 20.5384 19.422 20.2846 19.6053 19.9615L19.9718 19.3269L21.7244 16.2692C21.9077 15.9461 21.9993 15.5884 21.9993 15.2307H15.6987L17.0394 17.8846L18.8492 20.723Z" fill="#EA4335"></path><path d="M11.9982 8.76923L15.1483 3.27692C14.839 3.09231 14.4839 3 14.1173 3H9.87907C9.51252 3 9.15742 3.10385 8.84814 3.27692L11.9982 8.76923Z" fill="#00832D"></path><path d="M15.6983 15.2307H8.29849L5.14844 20.723C5.45772 20.9076 5.81281 20.9999 6.17937 20.9999H17.8174C18.1839 20.9999 18.539 20.8961 18.8483 20.723L15.6983 15.2307Z" fill="#2684FC"></path><path d="M18.8146 9.11532L15.9051 4.03839C15.7218 3.71532 15.4584 3.46147 15.1491 3.27686L11.999 8.76916L15.6989 15.2307H21.9876C21.9876 14.873 21.8959 14.5153 21.7127 14.1922L18.8146 9.11532Z" fill="#FFBA00"></path></svg>
                                      </i>
                                      Connect Google Drive
                                    </div>
                                    <div className='dropdown-item-box'>
                                      <i className='icon'>
                                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon" aria-hidden="true"><path d="M7.82373 7.35312L7.82391 7.35249L11.6726 9.65779L13.9659 8.6927C14.4319 8.49124 14.9344 8.38793 15.4421 8.38843C15.5267 8.38843 15.6103 8.39227 15.6935 8.39782C15.4178 7.32269 14.8357 6.35079 14.0181 5.60023C13.2004 4.84966 12.1823 4.35275 11.0876 4.16987C9.99283 3.98699 8.86853 4.12601 7.8513 4.57005C6.83408 5.01409 5.96772 5.74403 5.35751 6.67115C5.37712 6.67091 5.39643 6.66968 5.41608 6.66968C6.26666 6.66852 7.10061 6.90525 7.82373 7.35312Z" fill="#0364B8"></path><path d="M7.82449 7.3525L7.82431 7.35313C7.10119 6.90526 6.26724 6.66853 5.41666 6.66968C5.39701 6.66968 5.37767 6.67091 5.35809 6.67116C4.52559 6.68147 3.71163 6.91843 3.00368 7.35659C2.29574 7.79475 1.72058 8.41754 1.34 9.15804C0.959422 9.89853 0.787821 10.7287 0.843637 11.5594C0.899452 12.3901 1.18057 13.1899 1.65679 13.8728L5.05075 12.4446L6.55948 11.8097L9.91879 10.396L11.6731 9.65779L7.82449 7.3525Z" fill="#0078D4"></path><path d="M15.6938 8.39782C15.6106 8.39227 15.527 8.38843 15.4424 8.38843C14.9347 8.38793 14.4323 8.49163 13.9663 8.6931L11.6728 9.65779L12.3378 10.0561L14.5178 11.3619L15.4688 11.9316L18.7209 13.8795C19.0164 13.331 19.1695 12.717 19.1663 12.0939C19.163 11.4709 19.0035 10.8586 18.7023 10.3131C18.4011 9.76768 17.9678 9.30653 17.4422 8.97191C16.9166 8.63729 16.3154 8.4399 15.6938 8.39782Z" fill="#1490DF"></path><path d="M15.4693 11.9315L14.5182 11.3618L12.3383 10.0561L11.6732 9.65771L9.9189 10.396L6.55959 11.8096L5.05086 12.4445L1.6569 13.8727C2.07866 14.4791 2.64091 14.9744 3.29564 15.3163C3.95037 15.6583 4.67813 15.8367 5.41676 15.8363H15.4428C16.115 15.8365 16.7748 15.6547 17.352 15.3101C17.9292 14.9656 18.4024 14.4712 18.7213 13.8794L15.4693 11.9315Z" fill="#28A8EA"></path></svg>
                                      </i>
                                      Connect Microsoft OneDrive
                                      <span className='sub-text'>personal</span>
                                    </div>
                                    <div className='dropdown-item-box'>
                                      <i className='icon'>
                                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon" aria-hidden="true"><path d="M7.82373 7.35312L7.82391 7.35249L11.6726 9.65779L13.9659 8.6927C14.4319 8.49124 14.9344 8.38793 15.4421 8.38843C15.5267 8.38843 15.6103 8.39227 15.6935 8.39782C15.4178 7.32269 14.8357 6.35079 14.0181 5.60023C13.2004 4.84966 12.1823 4.35275 11.0876 4.16987C9.99283 3.98699 8.86853 4.12601 7.8513 4.57005C6.83408 5.01409 5.96772 5.74403 5.35751 6.67115C5.37712 6.67091 5.39643 6.66968 5.41608 6.66968C6.26666 6.66852 7.10061 6.90525 7.82373 7.35312Z" fill="#0364B8"></path><path d="M7.82449 7.3525L7.82431 7.35313C7.10119 6.90526 6.26724 6.66853 5.41666 6.66968C5.39701 6.66968 5.37767 6.67091 5.35809 6.67116C4.52559 6.68147 3.71163 6.91843 3.00368 7.35659C2.29574 7.79475 1.72058 8.41754 1.34 9.15804C0.959422 9.89853 0.787821 10.7287 0.843637 11.5594C0.899452 12.3901 1.18057 13.1899 1.65679 13.8728L5.05075 12.4446L6.55948 11.8097L9.91879 10.396L11.6731 9.65779L7.82449 7.3525Z" fill="#0078D4"></path><path d="M15.6938 8.39782C15.6106 8.39227 15.527 8.38843 15.4424 8.38843C14.9347 8.38793 14.4323 8.49163 13.9663 8.6931L11.6728 9.65779L12.3378 10.0561L14.5178 11.3619L15.4688 11.9316L18.7209 13.8795C19.0164 13.331 19.1695 12.717 19.1663 12.0939C19.163 11.4709 19.0035 10.8586 18.7023 10.3131C18.4011 9.76768 17.9678 9.30653 17.4422 8.97191C16.9166 8.63729 16.3154 8.4399 15.6938 8.39782Z" fill="#1490DF"></path><path d="M15.4693 11.9315L14.5182 11.3618L12.3383 10.0561L11.6732 9.65771L9.9189 10.396L6.55959 11.8096L5.05086 12.4445L1.6569 13.8727C2.07866 14.4791 2.64091 14.9744 3.29564 15.3163C3.95037 15.6583 4.67813 15.8367 5.41676 15.8363H15.4428C16.115 15.8365 16.7748 15.6547 17.352 15.3101C17.9292 14.9656 18.4024 14.4712 18.7213 13.8794L15.4693 11.9315Z" fill="#28A8EA"></path></svg>
                                      </i>
                                      Connect Microsoft OneDrive
                                      <span className='sub-text'>Work/School - Includes SharePoint</span>
                                    </div>
                                  </div>
                                </div> */}
                              </div>
                            }
                          </div>
                        )}
                      </div>
                    </div>
                    {isLoading ?
                      <div className='right-area'>
                        <button type="submit" className='speak-btn style-two' onClick={stop}>
                          <i className='icon fa fa-stop'></i>
                        </button>
                      </div>
                      :
                      <div className='right-area'>
                        <div className='add-mmore-file' >
                          <button type="button" className='tools-btn' onClick={() => setDropdownOpenFiles(!dropdownOpenFiles)}>
                            {selectedModel} <i className='icon fa fa-angle-down'></i>
                          </button>
                          {dropdownOpenFiles && (
                            <div className='add-files-dropdown' ref={dropdownRef}>
                              <div className='add-files-dropdown style-two'>
                                <div className='searchbox-dropdown'>
                                  <i className='icon'><svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.40156 10.6464C2.74292 10.6464 0.578369 8.48183 0.578369 5.82319C0.578369 3.16455 2.74292 1 5.40156 1C8.0602 1 10.2248 3.16455 10.2248 5.82319C10.2248 8.48183 8.0602 10.6464 5.40156 10.6464ZM5.40156 1.70583C3.12878 1.70583 1.2842 3.55511 1.2842 5.82319C1.2842 8.09127 3.12878 9.94055 5.40156 9.94055C7.67434 9.94055 9.51892 8.09127 9.51892 5.82319C9.51892 3.55511 7.67434 1.70583 5.40156 1.70583Z" fill="#50525870" stroke="#50525870" stroke-width="0.236842"></path><path d="M12.8959 13.7066C12.8021 13.7068 12.7083 13.6723 12.6342 13.5984L8.68189 9.63155C8.5386 9.48864 8.53829 9.2518 8.68119 9.10852C8.82409 8.96524 9.06094 8.96492 9.20422 9.10783L13.1565 13.0747C13.2998 13.2176 13.3001 13.4545 13.1572 13.5977C13.0833 13.6718 12.9896 13.7065 12.8959 13.7066Z" fill="#50525870" stroke="#50525870" stroke-width="0.236842"></path></svg></i>
                                  <TextField
                                    fullWidth
                                    hiddenLabel
                                    size='small'
                                    name='search'
                                    variant='standard'
                                    value={toolFilter}
                                    placeholder='Search...'
                                    autoFocus
                                    onChange={(e) => setToolFilter(e['target']['value'])}
                                  />
                                </div>
                                {(() => {
                                  const allFilteredModels = assistantModels.filter(model =>
                                    toolFilter === '' || model['name'].toLowerCase().includes(toolFilter.toLowerCase())
                                  );
                                  if (toolFilter && allFilteredModels.length === 0) {
                                    return <div className='dropdown-item-box' style={{color: '#888', textAlign: 'center'}}>"{toolFilter}" not found</div>;
                                  }
                                  return ['OpenAI', 'Claude'].map(category => {
                                    const categoryModels = allFilteredModels.filter(model => model['category'] === category);
                                    if (categoryModels.length === 0) return null;
                                    return (
                                      <React.Fragment key={category}>
                                        <h5 style={{marginTop: category !== 'OpenAI' ? '12px' : undefined}}>{category} Models</h5>
                                        {categoryModels.map(model => (
                                          <div key={model['id']}
                                               className='dropdown-item-box'
                                               onClick={() => handleModelSelect(model['id'])}
                                               style={{cursor: 'pointer'}}>
                                            {model['name']} {selectedModel === model['id'] && '✓'}
                                          </div>
                                        ))}
                                      </React.Fragment>
                                    );
                                  });
                                })()}
                              </div>
                            </div>
                          )}
                        </div>
                        <AudioInput
                          onTranscriptionComplete={handleTranscriptionComplete}
                          disabled={isLoading}
                        />

                        {input.trim().length > 0 ? (
                          <button type="submit" className='speak-btn style-two' disabled={isLoading}>
                            <i className='icon'>
                              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg" class="icon"><path d="M8.99992 16V6.41407L5.70696 9.70704C5.31643 10.0976 4.68342 10.0976 4.29289 9.70704C3.90237 9.31652 3.90237 8.6835 4.29289 8.29298L9.29289 3.29298L9.36907 3.22462C9.76184 2.90427 10.3408 2.92686 10.707 3.29298L15.707 8.29298L15.7753 8.36915C16.0957 8.76192 16.0731 9.34092 15.707 9.70704C15.3408 10.0732 14.7618 10.0958 14.3691 9.7754L14.2929 9.70704L10.9999 6.41407V16C10.9999 16.5523 10.5522 17 9.99992 17C9.44764 17 8.99992 16.5523 8.99992 16Z"></path></svg>
                            </i>
                          </button>
                        ) : (
                          <button type="submit" className='speak-btn' disabled={isLoading || !input.trim()}>
                            <i className='icon'>
                              <svg width="8" height="9" viewBox="0 0 8 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1.33532 5.83226C1.21394 5.83226 1.11328 5.7316 1.11328 5.61022V3.38983C1.11328 3.26844 1.21394 3.16779 1.33532 3.16779C1.4567 3.16779 1.55736 3.26844 1.55736 3.38983V5.61022C1.55736 5.7316 1.4567 5.83226 1.33532 5.83226Z" fill="#4B5563" />
                                <path d="M2.66735 6.57241C2.54597 6.57241 2.44531 6.47175 2.44531 6.35037V2.64971C2.44531 2.52833 2.54597 2.42767 2.66735 2.42767C2.78873 2.42767 2.88939 2.52833 2.88939 2.64971V6.35037C2.88939 6.47175 2.78873 6.57241 2.66735 6.57241Z" fill="#4B5563" />
                                <path d="M4.00036 7.3125C3.87898 7.3125 3.77832 7.21184 3.77832 7.09046V1.90954C3.77832 1.78816 3.87898 1.6875 4.00036 1.6875C4.12174 1.6875 4.2224 1.78816 4.2224 1.90954V7.09046C4.2224 7.21184 4.12174 7.3125 4.00036 7.3125Z" fill="#4B5563" />
                                <path d="M5.33239 6.57241C5.21101 6.57241 5.11035 6.47175 5.11035 6.35037V2.64971C5.11035 2.52833 5.21101 2.42767 5.33239 2.42767C5.45377 2.42767 5.55443 2.52833 5.55443 2.64971V6.35037C5.55443 6.47175 5.45377 6.57241 5.33239 6.57241Z" fill="#4B5563" />
                                <path d="M6.66442 5.83226C6.54304 5.83226 6.44238 5.7316 6.44238 5.61022V3.38983C6.44238 3.26844 6.54304 3.16779 6.66442 3.16779C6.7858 3.16779 6.88646 3.26844 6.88646 3.38983V5.61022C6.88646 5.7316 6.7858 5.83226 6.66442 5.83226Z" fill="#4B5563" />
                              </svg>
                            </i>
                          </button>
                        )}
                      </div>
                    }

                    {/* {isLoading
                      ? 
                      : <div className='right-area'>
                        <button className='tools-btn mic-btn' onClick={() => setShowVoice(!isVoiceShow)}>
                          
                        </button>
                        {isLoading ? (
                          <button type="submit" className='speak-btn' disabled={isLoading || !input.trim()}>
                            
                          </button>
                        ) : wordCount === 1 ? (
                          <button type="submit" className='speak-btn style-two' disabled={isLoading || !input.trim()}>
                            
                          </button>
                        ) : (
                          <button type="submit" className='speak-btn' disabled={isLoading || !input.trim()}>
                            <i className='icon'>
                              
                            </i>
                          </button>
                        )}
                      </div>
                    } */}
                  </div>
                </form>
              </div>
            </div>
          }

        </div >
      }
      <style jsx>{`
        .typing-indicator {
          display: flex;
          align-items: center;
        }
        .dots {
          display: inline-flex;
          margin-left: 4px;
        }
        .dots span {
          animation: pulse 1.5s infinite;
          opacity: 0.6;
        }
        .dots span:nth-child(2) {
          animation-delay: 0.2s;
        }
        .dots span:nth-child(3) {
          animation-delay: 0.4s;
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        /* Enhanced markdown content styling */
        .markdown-content {
              /* line-height: 1.1; */
              line-height: 1.6;
              color: inherit;
          /* word-break: break-words; */
          word-break: break-word;
          white-space: normal;
          overflow-wrap: break-word;
          text-align: left;
          display: block;
        }
        
        .formatted-content {
              /* line-height: 1.1; */
              line-height: 1.6;
              color: inherit;
          /* word-break: break-words; */
          word-break: break-word;
          white-space: normal;
          overflow-wrap: break-word;
          text-align: left;
          display: block;
        }
        
        .formatted-content p,
        .markdown-content p {
          /* line-height: 1.2; */
          line-height: 1.6;
          color: inherit;
          /* margin: 0; */
          margin: 0.5rem 0;
          white-space: normal;
          overflow-wrap: break-word;
          display: block;
        }
        
        .formatted-content strong,
        .markdown-content strong {
          font-weight: 600;
        }
        
        .formatted-content em,
        .markdown-content em {
          font-style: italic;
        }
        
        .formatted-content ul,
        .markdown-content ul {
          margin: 0.2rem 0;
          padding-left: 1.2rem;
          list-style-type: disc;
          line-height: 1.2;
          list-style-position: outside;
        }
        
        .formatted-content ol,
        .markdown-content ol {
          margin: 0.2rem 0;
          padding-left: 1.2rem;
          list-style-type: decimal;
          line-height: 1.2;
          list-style-position: outside;
        }
        
        .formatted-content li,
        .markdown-content li {
          /* margin: 0.1rem 0; */
          margin: 0.3rem 0;
          /* line-height: 1.2; */
          line-height: 1.6;
          display: list-item;
          word-break: break-word;
          white-space: normal;
          overflow-wrap: break-word;
        }
        
        .formatted-content h1,
        .markdown-content h1 {
          font-size: 1.3rem;
          font-weight: 600;
          margin: 0.4rem 0 0.2rem;
          color: inherit;
          line-height: 1.2;
        }
        
        .formatted-content h2,
        .markdown-content h2 {
          font-size: 1.2rem;
          font-weight: 600;
          margin: 0.3rem 0 0.15rem;
          color: inherit;
          line-height: 1.2;
        }
        
        .formatted-content h3,
        .markdown-content h3 {
          font-size: 1.1rem;
          font-weight: 600;
          margin: 0.25rem 0 0.1rem;
          color: inherit;
          line-height: 1.2;
        }
        
        .formatted-content blockquote,
        .markdown-content blockquote {
          border-left: 4px solid #CAD6E4;
          padding-left: 1rem;
          margin: 0.5rem 0;
          color: #64748b;
          font-style: italic;
          background-color: rgba(0,0,0,0.02);
          border-radius: 0.5rem;
          padding: 0.5rem 0.5rem 0.5rem 1rem;
        }
        
        .formatted-content code,
        .markdown-content code {
          background-color: #f1f5f9;
          padding: 0.15rem 0.3rem;
          border-radius: 0.25rem;
          font-family: ui-monospace, SFMono-Regular, "SF Mono", Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          font-size: 0.85rem;
          border: 1px solid #CAD6E4;
        }
        
        .formatted-content pre,
        .markdown-content pre {
          background-color: #f8fafc;
          padding: 0.5rem;
          border-radius: 0.5rem;
          overflow: auto;
          font-family: ui-monospace, SFMono-Regular, "SF Mono", Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          margin: 0.5rem 0;
          border: 1px solid #CAD6E4;
        }
        
        .formatted-content pre code,
        .markdown-content pre code {
          background: none;
          padding: 0;
          border: none;
        }
        
        /* Chat message container styles to ensure proper markdown rendering */
        .chat-box-color {
          word-wrap: break-word;
          overflow-wrap: break-word;
          white-space: normal;
          overflow: visible;
          display: block;
          width: 100%;
          max-width: 100%;
          text-align: left;
          line-height: 1.6;
        }
        
        /* Ensure list items render properly within chat messages */
        .chat-box-color ul,
        .chat-box-color ol {
          margin: 0.2rem 0;
          padding-left: 1.2rem;
        }
        
        .chat-box-color ul {
          list-style-type: disc;
          list-style-position: inside;
        }
        
        .chat-box-color ol {
          list-style-type: decimal;
          list-style-position: inside;
        }
        
        .chat-box-color li {
          /* margin: 0.1rem 0; */
          margin: 0.3rem 0;
          /* line-height: 1.3; */
          line-height: 1.6;
          display: list-item;
          word-break: break-word;
          white-space: normal;
          overflow-wrap: break-word;
          padding-left: 0.25rem;
        }
      `}</style>
      <MCPManager open={mcpManagerOpen} onClose={() => setMcpManagerOpen(false)} />
    </>
  );
};

export default Assistant;